import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ShoppingCart,
  SlidersHorizontal,
  Star,
  Clock,
  Info,
  Heart,
  Sparkles,
  Check,
  X,
  Languages,
  Settings,
  Grid3x3,
  List,
  Leaf,
  Wheat,
  Plus,
  Minus,
  ArrowLeft,
  ShoppingBag,
  CreditCard,
  Banknote,
  ChevronDown,
  Mail,
  Shield,
  Smartphone,
  Send,
  BrainCircuit,
  Phone,
  AlertTriangle,
} from "lucide-react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  createPublicOrder,
  getPublicTable,
  getRecommendationSignals,
  syncGuestProfile,
  fetchRecommendationScores,
  fetchAttachRecommendations,
  type CreatePublicOrderPayload,
  type CreatePublicOrderResponse,
  type CreatePublicOrderResult,
  type PublicTableItem,
  type PublicTableResponse,
  type PublicRecommendationSignal,
  type PublicLoyaltyProgramConfig,
  recordRecommendationImpression,
  recordRecommendationAction,
  type RecommendationImpressionPayload,
} from "@/lib/api/public";
import {
  sendPublicAssistantMessage,
  fetchAssistantFeatureConfig,
  type PublicAssistantAction,
  type PublicAssistantSafety,
  type PublicAssistantFeatureConfig,
  type ResolutionTier,
} from "@/lib/api/publicAiAssistant";
import { matchGuestIntent } from "@/lib/assistant/intentGuard";
import { ApiResponseError } from "@/lib/api/response";
import { useToast } from "@/hooks/use-toast";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  RecommendationBreakdown as SharedRecommendationBreakdown,
  RecommendationSource as SharedRecommendationSource,
  RecommendationState as SharedRecommendationState,
  buildGuestTasteSnapshot,
  buildRecommendationState,
  computePopularFallback,
  extractSnapshotItemIds,
  normaliseTasteString,
  resolvePersonalizationConsent,
  toUniqueStringList,
  coerceNumberRecord,
  mergeNumberRecords,
} from "@/lib/recommendations/engine";
import { buildDemoTableResponse } from "./tableOrderDemoData";

type ViewState = "menu" | "item-customization" | "cart" | "checkout" | "confirmation";
type SortOption = "featured" | "price-asc" | "price-desc" | "prep-time";
type DietFilter = "all" | "vegetarian" | "vegan";
type TextSize = "base" | "large";

const generateMessageId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

interface MenuModifier {
  id: string;
  name: string;
  priceDelta: number;
  groupId?: string | null;
  groupName?: string | null;
  isDefault?: boolean;
}

interface MenuModifierGroup {
  id: string;
  name: string;
  helperText?: string | null;
  selectionMode: "single" | "multiple";
  minSelections: number;
  maxSelections: number | null;
  isRequired: boolean;
  allowQuantity: boolean;
  kind: "variant" | "addon" | "topping" | "combo";
  modifiers: MenuModifier[];
  isFallback?: boolean;
}

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  categoryId: string;
  categoryName: string;
  dietary: DietFilter[];
  image: string;
  popular?: boolean;
  prepTime: number;
  marginCents?: number | null;
  station?: string | null;
  isNew?: boolean | null;
  modifiers: MenuModifier[];
  modifierGroups: MenuModifierGroup[];
  tags?: string[];
  cuisines?: string[];
  tasteHeat?: string | null;
}

interface CartItem {
  item: MenuItem;
  quantity: number;
  modifiers: MenuModifier[];
  notes: string;
}

interface AttachRecommendationItem {
  item: MenuItem;
  probability: number;
  matches: Array<{ mainItemId: string; probability: number }>;
}

interface GuestProfile {
  favorites: string[];
  name?: string;
  phone?: string;
  externalId?: string | null;
  tasteTags?: string[];
  tasteCuisines?: string[];
  tasteHeat?: string | null;
  consentPersonalization?: boolean;
}

type RecommendationActionType = "add_item" | "open_customization" | "order_item";

type RecommendationBreakdown = SharedRecommendationBreakdown;
type RecommendationSource = SharedRecommendationSource;
type RecommendationState = SharedRecommendationState<MenuItem>;

const TEN_DIGIT_PHONE_LENGTH = 10;
const CLIENT_RECOMMENDATION_MODEL_VERSION = "qr-client-heuristic/v2";
const ATTACH_MODEL_VERSION = "attach-rail/v1";
const ATTACH_SURFACE = "table_order.attach";
const extractPhoneDigits = (value: string): string => value.replace(/\D/g, "");
const normalizePhoneInputValue = (value: string): string => {
  const digits = extractPhoneDigits(value);
  if (digits.length <= TEN_DIGIT_PHONE_LENGTH) {
    return digits;
  }
  return digits.slice(digits.length - TEN_DIGIT_PHONE_LENGTH);
};

const humanizeTenantSlug = (slug: string): string =>
  slug
    .split(/[-_]/g)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

const coerceFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const normalizeModifier = (
  input: unknown,
  fallbackGroupId: string,
  fallbackGroupName?: string,
): MenuModifier | null => {
  if (!input || typeof input !== "object") {
    return null;
  }
  const record = input as Record<string, unknown>;
  const idCandidate =
    (typeof record.id === "string" && record.id.trim().length > 0 && record.id.trim()) ||
    (typeof record.modifier_id === "string" && record.modifier_id.trim().length > 0 && record.modifier_id.trim()) ||
    (typeof record.modifierId === "string" && record.modifierId.trim().length > 0 && record.modifierId.trim()) ||
    (typeof record.code === "string" && record.code.trim().length > 0 && record.code.trim());
  const nameCandidate =
    (typeof record.name === "string" && record.name.trim().length > 0 && record.name.trim()) ||
    (typeof record.label === "string" && record.label.trim().length > 0 && record.label.trim());
  if (!idCandidate || !nameCandidate) {
    return null;
  }
  const centsValue =
    coerceFiniteNumber(record.price_delta_cents) ??
    coerceFiniteNumber(record.priceDeltaCents) ??
    coerceFiniteNumber(record.price_cents) ??
    coerceFiniteNumber(record.priceCents);
  const directValue = coerceFiniteNumber(record.price) ?? coerceFiniteNumber(record.amount);
  const priceDelta =
    typeof centsValue === "number" ? centsValue / 100 : typeof directValue === "number" ? directValue : 0;
  const groupId =
    (typeof record.group_id === "string" && record.group_id.trim().length > 0 && record.group_id.trim()) ||
    (typeof record.groupId === "string" && record.groupId.trim().length > 0 && record.groupId.trim()) ||
    fallbackGroupId;
  const groupName =
    (typeof record.group_name === "string" && record.group_name.trim().length > 0 && record.group_name.trim()) ||
    (typeof record.groupName === "string" && record.groupName.trim().length > 0 && record.groupName.trim()) ||
    fallbackGroupName ||
    null;
  const isDefault = Boolean(record.is_default ?? record.default ?? record.isDefault);
  return {
    id: idCandidate,
    name: nameCandidate,
    priceDelta,
    groupId,
    groupName,
    isDefault,
  };
};

const normalizeModifierList = (
  input: unknown,
  fallbackGroupId: string,
  fallbackGroupName?: string,
): MenuModifier[] => {
  if (!Array.isArray(input)) {
    return [];
  }
  const entries: MenuModifier[] = [];
  input.forEach((entry) => {
    const normalized = normalizeModifier(entry, fallbackGroupId, fallbackGroupName);
    if (normalized) {
      entries.push(normalized);
    }
  });
  return entries;
};

const normalizeModifierGroupRecord = (
  item: PublicTableItem,
  input: unknown,
  index: number,
): MenuModifierGroup | null => {
  if (!input || typeof input !== "object") {
    return null;
  }
  const record = input as Record<string, unknown>;
  const idCandidate =
    (typeof record.id === "string" && record.id.trim().length > 0 && record.id.trim()) ||
    (typeof record.code === "string" && record.code.trim().length > 0 && record.code.trim()) ||
    `${item.id}-group-${index}`;
  const nameCandidate =
    (typeof record.name === "string" && record.name.trim().length > 0 && record.name.trim()) ||
    (typeof record.label === "string" && record.label.trim().length > 0 && record.label.trim()) ||
    `Group ${index + 1}`;
  const helperText =
    typeof record.description === "string" && record.description.trim().length > 0 ? record.description.trim() : null;
  const selectionModeRaw =
    (typeof record.selection_mode === "string" && record.selection_mode.trim().length > 0 && record.selection_mode.trim()) ||
    (typeof record.selectionMode === "string" && record.selectionMode.trim().length > 0 && record.selectionMode.trim()) ||
    null;
  const selectionMode = selectionModeRaw === "single" ? "single" : "multiple";
  const minSelections = Math.max(
    0,
    Math.trunc(coerceFiniteNumber(record.min_selections ?? record.minSelections ?? 0) ?? 0),
  );
  const maxSelectionsValue = coerceFiniteNumber(record.max_selections ?? record.maxSelections ?? null);
  const maxSelections =
    typeof maxSelectionsValue === "number" && Number.isFinite(maxSelectionsValue) && maxSelectionsValue > 0
      ? Math.trunc(maxSelectionsValue)
      : null;
  const isRequired = Boolean(record.is_required ?? record.required ?? minSelections > 0);
  const allowQuantity = Boolean(record.allow_quantity ?? record.allowQuantity);
  const kindValue =
    (typeof record.kind === "string" && record.kind.trim().length > 0 && record.kind.trim()) || "addon";
  const modifiers = normalizeModifierList(record.modifiers, idCandidate, nameCandidate);
  if (modifiers.length === 0) {
    return null;
  }
  return {
    id: idCandidate,
    name: nameCandidate,
    helperText,
    selectionMode,
    minSelections,
    maxSelections,
    isRequired,
    allowQuantity,
    kind: (kindValue as MenuModifierGroup["kind"]) ?? "addon",
    modifiers,
  };
};

const normalizeModifierGroups = (item: PublicTableItem): MenuModifierGroup[] => {
  const metadata = item.metadata ?? {};
  const candidates = [
    (item as { modifier_groups?: unknown }).modifier_groups,
    (metadata as { modifier_groups?: unknown }).modifier_groups,
    (metadata as { modifierGroups?: unknown }).modifierGroups,
  ];
  for (const source of candidates) {
    if (Array.isArray(source) && source.length > 0) {
      const groups = source
        .map((group, index) => normalizeModifierGroupRecord(item, group, index))
        .filter((group): group is MenuModifierGroup => Boolean(group));
      if (groups.length > 0) {
        return groups;
      }
    }
  }
  const fallbackSource =
    (Array.isArray(item.modifiers) && item.modifiers.length > 0
      ? item.modifiers
      : (metadata as { modifiers?: unknown }).modifiers) ?? [];
  const fallbackModifiers = normalizeModifierList(fallbackSource, `${item.id}-addons`, "Add-ons");
  if (fallbackModifiers.length > 0) {
    return [
      {
        id: `${item.id}-fallback-group`,
        name: "Add-ons",
        helperText: null,
        selectionMode: "multiple",
        minSelections: 0,
        maxSelections: null,
        isRequired: false,
        allowQuantity: false,
        kind: "addon",
        modifiers: fallbackModifiers,
        isFallback: true,
      },
    ];
  }
  return [];
};

const formatTemplate = (template: string, replacements: Record<string, string | number>): string =>
  template.replace(/{{(.*?)}}/g, (_, key: string) => String(replacements[key.trim()] ?? ""));

interface CategoryOption {
  id: string;
  name: string;
}

interface PlaceOrderRequest {
  fulfillment: "to-go" | "dine-in" | "delivery";
  guests: number;
  name: string;
  email: string;
  paymentMethod: "card" | "cash";
  tipAmount: number;
  discountAmount: number;
  promoCode: string | null;
  scheduleOrder: boolean;
  readyWindow: string;
  savePreferences: boolean;
  loyaltyUpdates: boolean;
  emailUpdates: boolean;
  textUpdates: boolean;
  orderNote: string;
   loyaltyRedeemPoints: number;
}

interface PlaceOrderResult {
  orderNumber: string;
  placedAt: string;
  estimatedReady: string | null;
  cart: CartItem[];
  totals: {
    subtotal: number;
    tax: number;
    tip: number;
    discount: number;
    total: number;
  };
}

interface VenueInfo {
  name: string;
  rating?: number | null;
  reviewCount?: number | null;
  prepTime?: string | null;
  location?: string | null;
  tableNumber?: string | null;
}

const DEFAULT_VENUE_INFO: VenueInfo = {
  name: "Bella Vista",
  rating: 4.8,
  reviewCount: 328,
  prepTime: "15-20 min",
  location: "123 Lakeside Avenue, Springfield",
  tableNumber: "12",
};

const ANON_TENANT = "demo";
const DEFAULT_TABLE = "table";

interface TableOrderDiagnosticsSnapshot {
  tenantSlug: string;
  tableSlug: string;
  tokenPresent: boolean;
  tokenPreview: string | null;
  phoneVerified: boolean;
  phonePreview: string | null;
  guestTokenPresent: boolean;
  isDemoMode: boolean;
  storageKeys: {
    phone: string;
    language: string;
    favorites: string;
    assistant: string;
    guestToken: string;
  };
  timestamp: string;
}

type TableOrderLinkIssue = "tenant" | "table" | "token";

const LINK_ISSUE_CODES: Record<TableOrderLinkIssue, string> = {
  tenant: "QR.LINK_TENANT_MISSING",
  table: "QR.LINK_TABLE_MISSING",
  token: "QR.TOKEN_MISSING",
};

declare global {
  interface Window {
    __BRAISE_TABLE_ORDER_DIAGNOSTICS__?: TableOrderDiagnosticsSnapshot;
  }
}

const maskTokenPreview = (value: string): string | null => {
  if (!value) return null;
  const normalized = value.trim();
  if (normalized.length <= 8) {
    return normalized.replace(/./g, "•");
  }
  return `${normalized.slice(0, 4)}…${normalized.slice(-4)}`;
};

const maskPhonePreview = (value: string): string | null => {
  if (!value) return null;
  const normalized = value.trim();
  if (normalized.length <= 4) {
    return "••••";
  }
  return `${normalized.slice(0, 2)}•••${normalized.slice(-2)}`;
};

const buildStorageKey = (tenant: string, table: string, suffix: string) =>
  `braise:public:${tenant || ANON_TENANT}:${table || DEFAULT_TABLE}:${suffix}`;

const buildTenantStorageKey = (tenant: string, suffix: string) =>
  `braise:public:${tenant || ANON_TENANT}:${suffix}`;

function cn(...classes: Array<string | undefined | false>) {
  return classes.filter(Boolean).join(" ");
}

interface ImageWithFallbackProps {
  src: string;
  alt: string;
  className?: string;
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({ src, alt, className }) => {
  const [failed, setFailed] = useState(false);
  const fallback =
    "https://images.unsplash.com/photo-1529042410759-befb1204b468?auto=format&fit=crop&w=800&q=80";

  return (
    <img
      src={failed ? fallback : src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

// ============================================================================
// UI COMPONENTS (All Inlined)
// ============================================================================

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className = "", variant = "default", size = "default", ...props }, ref) => {
    const baseStyles =
      "inline-flex items-center justify-center rounded-lg transition-colors disabled:opacity-50 disabled:pointer-events-none";
    const variants = {
      default: "bg-zinc-900 text-white hover:bg-zinc-800",
      outline: "border border-zinc-200 bg-white hover:bg-zinc-50",
      ghost: "hover:bg-zinc-100",
    } as const;
    const sizes = {
      default: "h-10 px-4 py-2",
      sm: "h-8 px-3 text-sm",
      lg: "h-12 px-6",
      icon: "h-10 w-10",
    } as const;

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className = "", ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        "flex h-10 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = "Input";

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: "default" | "secondary";
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className = "", variant = "default", ...props }, ref) => {
    const variants = {
      default: "bg-zinc-900 text-white",
      secondary: "bg-zinc-100 text-zinc-900",
    } as const;

    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs transition-colors",
          variants[variant],
          className,
        )}
        {...props}
      />
    );
  },
);
Badge.displayName = "Badge";

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className = "", ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        "flex min-h-[60px] w-full resize-none rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = "Textarea";

interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {}

const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ className = "", ...props }, ref) => (
    <label
      ref={ref}
      className={cn("text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", className)}
      {...props}
    />
  ),
);
Label.displayName = "Label";

const Separator = ({ className = "" }: { className?: string }) => (
  <div className={cn("h-px bg-zinc-200", className)} />
);

interface CheckboxProps {
  id?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}

const Checkbox: React.FC<CheckboxProps> = ({ id, checked, onCheckedChange, disabled }) => (
  <button
    id={id}
    type="button"
    role="checkbox"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onCheckedChange(!checked)}
    className={cn(
      "peer h-4 w-4 shrink-0 rounded border transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2",
      checked ? "bg-zinc-900 border-zinc-900 text-white" : "border-zinc-300 bg-white",
      disabled && "cursor-not-allowed opacity-50",
    )}
  >
    {checked && <Check className="h-3 w-3 text-white" />}
  </button>
);

interface RadioGroupItemProps {
  value: string;
  id: string;
  checked: boolean;
  onChange: () => void;
}

const RadioGroupItem: React.FC<RadioGroupItemProps> = ({ id, checked, onChange }) => (
  <button
    type="button"
    role="radio"
    aria-checked={checked}
    id={id}
    onClick={onChange}
    className="aspect-square h-4 w-4 rounded-full border border-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2"
  >
    {checked && (
      <div className="flex h-full items-center justify-center">
        <div className="h-2 w-2 rounded-full bg-zinc-900" />
      </div>
    )}
  </button>
);

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}

const Select: React.FC<SelectProps> = ({ value, onValueChange, children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child, {
          open,
          setOpen,
          value,
          onValueChange,
        });
      })}
    </div>
  );
};

interface SelectTriggerProps {
  className?: string;
  children: React.ReactNode;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

const SelectTrigger: React.FC<SelectTriggerProps> = ({ className = "", children, open, setOpen }) => (
  <button
    type="button"
    onClick={() => setOpen?.(!open)}
    className={cn(
      "flex h-10 w-full items-center justify-between rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2",
      className,
    )}
  >
    {children}
    <ChevronDown className="h-4 w-4 opacity-50" />
  </button>
);

const SelectValue: React.FC<{ value?: string }> = ({ value }) => <span>{value}</span>;

interface SelectContentProps {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  onValueChange?: (value: string) => void;
}

const SelectContent: React.FC<SelectContentProps> = ({ children, open, setOpen, onValueChange }) => {
  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={() => setOpen?.(false)} />
      <div className="absolute z-50 mt-1 w-full rounded-lg border border-zinc-200 bg-white shadow-lg">
        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) return child;
          return React.cloneElement(child, { onValueChange, setOpen });
        })}
      </div>
    </>
  );
};

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  onValueChange?: (value: string) => void;
  setOpen?: (open: boolean) => void;
}

const SelectItem: React.FC<SelectItemProps> = ({ value, children, onValueChange, setOpen }) => (
  <button
    type="button"
    onClick={() => {
      onValueChange?.(value);
      setOpen?.(false);
    }}
    className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-50"
  >
    {children}
  </button>
);

const CollapsibleContext = React.createContext<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}>({ open: false, onOpenChange: () => {} });

interface CollapsibleProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

const Collapsible: React.FC<CollapsibleProps> = ({ open, onOpenChange, children }) => (
  <CollapsibleContext.Provider value={{ open, onOpenChange }}>
    <div>{children}</div>
  </CollapsibleContext.Provider>
);

const CollapsibleTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }
>(({ asChild = false, onClick, children, ...props }, ref) => {
  const { open, onOpenChange } = React.useContext(CollapsibleContext);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented) {
      onOpenChange(!open);
    }
  };

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement;

    return React.cloneElement(child, {
      ...props,
      ref,
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        if (typeof child.props.onClick === "function") {
          child.props.onClick(event);
        }
        handleClick(event);
      },
    });
  }

  return (
    <button ref={ref} onClick={handleClick} {...props}>
      {children}
    </button>
  );
});
CollapsibleTrigger.displayName = "CollapsibleTrigger";

const CollapsibleContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { open } = React.useContext(CollapsibleContext);
  if (!open) return null;
  return <div>{children}</div>;
};

const toast = {
  success: (message: string) => {
    console.log("Success:", message);
  },
  error: (message: string) => {
    console.log("Error:", message);
  },
};

interface MenuPageProps {
  items: MenuItem[];
  categories: CategoryOption[];
  venue?: VenueInfo;
  phoneNumber: string;
  onUpdatePhone: (phone: string) => void;
  cartItemCount: number;
  cart: CartItem[];
  orderNote: string;
  onOrderNoteChange: (note: string) => void;
  onAddToCart: (item: MenuItem, quantity: number, modifiers: string[], notes: string) => void;
  onUpdateCartItem: (index: number, quantity: number) => void;
  onRemoveFromCart: (index: number) => void;
  onPlaceOrder: (orderData: PlaceOrderRequest) => Promise<PlaceOrderResult>;
  guestProfile: GuestProfile | null;
  setGuestProfile: (profile: GuestProfile) => void;
  language: "en" | "es";
  setLanguage: (lang: "en" | "es") => void;
  subtotal: number;
  recommendations?: {
    items: MenuItem[];
    source: RecommendationSource;
    breakdown: Record<string, RecommendationBreakdown>;
    consentPersonalization: boolean;
    onAction?: (itemId: string, action: RecommendationActionType) => void;
  };
  attachRecommendations?: {
    items: AttachRecommendationItem[];
    onAction?: (itemId: string, action: RecommendationActionType) => void;
  };
  loyaltyProgram?: PublicLoyaltyProgramConfig | null;
  loyaltyBalance?: number | null;
}

function MenuPage({
  items,
  categories,
  venue = DEFAULT_VENUE_INFO,
  phoneNumber,
  onUpdatePhone,
  cartItemCount,
  cart,
  orderNote,
  onOrderNoteChange,
  onAddToCart,
  onUpdateCartItem,
  onRemoveFromCart,
  onPlaceOrder,
  guestProfile,
  setGuestProfile,
  language,
  setLanguage,
  subtotal,
  recommendations,
  attachRecommendations,
  loyaltyProgram,
  loyaltyBalance,
}: MenuPageProps) {
  const [currentView, setCurrentView] = useState<ViewState>("menu");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [dietFilter, setDietFilter] = useState<DietFilter>("all");
  const [sortBy, setSortBy] = useState<SortOption>("featured");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [customizationQuantity, setCustomizationQuantity] = useState(1);
  const [selectedModifiers, setSelectedModifiers] = useState<string[]>([]);
  const [customizationNotes, setCustomizationNotes] = useState("");

  useEffect(() => {
    if (!selectedItem) {
      setSelectedModifiers([]);
      return;
    }
    const defaults =
      selectedItem.modifierGroups?.flatMap((group) =>
        group.modifiers.filter((modifier) => modifier.isDefault).map((modifier) => modifier.id),
      ) ?? [];
    setSelectedModifiers(defaults);
  }, [selectedItem]);

  const [fulfillment, setFulfillment] = useState<"to-go" | "dine-in" | "delivery">("to-go");
  const [guests, setGuests] = useState("1");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"card" | "cash">("cash");
  const [tipPercentage, setTipPercentage] = useState(18);
  const [customTip, setCustomTip] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [savePreferences, setSavePreferences] = useState(false);
  const [loyaltyUpdates, setLoyaltyUpdates] = useState(false);
  const [emailUpdates, setEmailUpdates] = useState(false);
  const [textUpdates, setTextUpdates] = useState(true);
  const [scheduleOrder, setScheduleOrder] = useState(true);
  const [readyWindow, setReadyWindow] = useState("asap");
  const [orderSummaryOpen, setOrderSummaryOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [secureCheckoutOpen, setSecureCheckoutOpen] = useState(false);
  const [orderAccuracyOpen, setOrderAccuracyOpen] = useState(false);
  const [prepTimelineOpen, setPrepTimelineOpen] = useState(false);

  const [orderNumber, setOrderNumber] = useState("");
  const [orderTime, setOrderTime] = useState("");
  const [estimatedReadyTime, setEstimatedReadyTime] = useState("");

  const [textSize, setTextSize] = useState<TextSize>("base");
  const [reduceMotion, setReduceMotion] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loyaltyRedeemInput, setLoyaltyRedeemInput] = useState("");
  const [confirmationCart, setConfirmationCart] = useState<CartItem[]>([]);
  const [confirmationTotals, setConfirmationTotals] = useState<PlaceOrderResult["totals"] | null>(null);

  const content = useMemo(
    () => ({
      en: {
        search: "Search",
        table: "Table",
        sort: "Sort & Filter",
        sortFeatured: "Featured",
        sortPriceAsc: "Price: Low to High",
        sortPriceDesc: "Price: High to Low",
        sortPrep: "Fastest Prep",
      favorites: "Favorites Only",
      recommended: "For You",
      attachHeading: "Popular pairings",
      attachSubheading: "Guests often add these sides after picking a main.",
      attachAction: "Add",
      attachRateSuffix: "attach rate",
      attachMatchesLabel: "Common with",
      popularCategory: "Popular",
      allDiet: "All Items",
        vegetarian: "Vegetarian",
        vegan: "Vegan",
        clearFilters: "Clear All",
        applyFilters: "Apply",
        items: "items",
        noResults: "No items found",
        resetFilters: "Clear filters",
        min: "min",
        cart: "Cart",
        empty: "Your cart is empty",
        emptyDesc: "Add items from the menu",
        browseMenu: "Browse Menu",
        orderNoteLabel: "Special requests",
        orderNotePlaceholder: "Allergies, preferences, etc...",
        subtotalLabel: "Subtotal",
        taxNotice: "Tax & fees calculated at checkout",
        checkout: "Checkout",
        customize: "Customize",
        addOnsTitle: "Perfect toppings & add-ons",
        addOnsHelper: "Tap to add toppings and extras for this dish.",
        noAddOns: "This item does not include extra toppings yet.",
        requiredLabel: "Required",
        optionalLabel: "Optional",
        chooseOne: "Choose one",
        chooseUpTo: "Choose up to {{count}}",
        chooseAtLeast: "Choose at least {{count}}",
        chooseBetween: "Choose between {{min}}-{{max}}",
        maxSelectionReached: "Maximum selections reached",
        selectionRequirementTitle: "Add required toppings",
        selectionRequirement: "Select at least {{count}} option(s) in {{group}}",
        selectedCount: "{{count}} selected",
        included: "Included",
        quantity: "Quantity",
        specialInstructions: "Special requests",
        placeholder: "Allergies, preferences...",
        add: "Add to Cart",
        optional: "Optional",
        fulfillmentLabel: "Fulfillment",
        toGo: "To-go",
        dineIn: "Dine-in",
        delivery: "Delivery",
        guests: "Guests",
        guestsHint: "How many seats?",
        nameLabel: "Name",
        phoneLabel: "Phone",
        emailLabel: "Email",
        updatePhone: "Update phone",
        textUpdatesHint: "We will text updates to this number.",
        emailHint: "We'll send order updates and receipts if you share your email.",
        preferences: "Preferences",
        saveFavorites: "Remember my favorites and preferences on this device.",
        saveFavoritesHint: "We'll only save these when you give consent.",
        loyaltyUpdates: "Send me loyalty credit updates and rewards.",
        loyaltyHint: "We'll only use your info to track points for this venue.",
        loyaltyBalanceLabel: "Loyalty balance",
        loyaltyRedeemLabel: "Use points on this order",
        loyaltyRedeemHint: "You can redeem up to {{points}} pts today.",
        emailOrderUpdates: "Send me email updates about my order.",
        emailOrderHint: "Add an email address to get updates.",
        textOrderUpdates: "Text me order updates.",
        textHint: "Message and data rates may apply.",
        scheduleOrder: "I authorize scheduling this order for later pickup.",
        scheduleHint: "Enable scheduling and choose a ready time that works for you.",
        readyWindow: "Preferred ready window",
        asap: "As soon as possible",
        in30: "In 30 minutes",
        in60: "In 1 hour",
        in90: "In 1.5 hours",
        orderSummary: "Order Summary",
        itemLabel: "item",
        payment: "Payment method",
        applePay: "Apple Pay",
        googlePay: "Google Pay",
        cardPay: "Credit / debit card",
        cash: "Cash",
        comingSoon: "(coming soon)",
        summary: "Summary",
        tip: "Tip",
        tipSuggestion: "Great service!",
        customTipLabel: "Custom",
        discountLabel: "Discount",
        estimatedTaxes: "Estimated taxes",
        total: "Total",
        placeOrder: "Place Order",
        promoCodeLabel: "Promo code",
        apply: "Apply",
        remove: "Remove",
        gutCheck: "Need a quick gut-check?",
        secureCheckout: "Secure checkout",
        orderAccuracy: "Order accuracy tips",
        orderAccuracyText:
          "Need to tweak something? Tap any item in your cart to adjust modifiers or notes before you submit.",
        prepTimeline: "Prep timeline",
        orderConfirmed: "Order Confirmed!",
        orderNumberLabel: "Order Number",
        orderPlaced: "Order placed at",
        estimatedReady: "Estimated ready time",
        contactInfo: "Contact Information",
        fulfillmentType: "Fulfillment Type",
        dineInLabel: "Dine In",
        toGoLabel: "To Go",
        deliveryLabel: "Delivery",
        trackOrder: "Track Order",
        backToMenu: "Back to Menu",
        thankYou: "Thank you for your order!",
        accessibility: "Accessibility",
        accessibilityDescription: "Adjust display preferences for this menu.",
        accessibilitySaveLabel: "Save comfort settings",
        accessibilitySaveDescription: "We'll use these preferences on this device.",
        accessibilitySaveButton: "Save preferences",
        accessibilitySavedToast: "Comfort settings saved",
        accessibilityLivePreview: "Live preview",
        textSizeLabel: "Text Size",
        textSizeHelper: "Choose the reading size that feels comfortable.",
        standard: "Standard",
        large: "Large",
        textSizeStandardDescription: "Balanced for browsing the entire menu.",
        textSizeLargeDescription: "Bigger labels and spacing for extra clarity.",
        comfortSettingsTitle: "Comfort boosts",
        comfortSettingsDescription: "Calm motion and amplify outlines when you need focus.",
        reduceMotion: "Reduce motion",
        reduceMotionDescription: "Softens looping animations and sliding transitions.",
        highContrastLabel: "High contrast focus outlines",
        highContrastDescription: "Highlights focus rings so keyboard navigation stands out.",
      },
      es: {
        search: "Buscar",
        table: "Mesa",
        sort: "Ordenar y Filtrar",
        sortFeatured: "Destacado",
        sortPriceAsc: "Precio: Bajo a Alto",
        sortPriceDesc: "Precio: Alto a Bajo",
        sortPrep: "Preparación Rápida",
      favorites: "Solo Favoritos",
      recommended: "Para Ti",
      attachHeading: "Combinaciones populares",
      attachSubheading: "Los invitados suelen agregar estos acompañamientos después de elegir un plato fuerte.",
      attachAction: "Agregar",
      attachRateSuffix: "tasa de adjunto",
      attachMatchesLabel: "Se combina con",
      popularCategory: "Populares",
      allDiet: "Todos",
        vegetarian: "Vegetariano",
        vegan: "Vegano",
        clearFilters: "Borrar Todo",
        applyFilters: "Aplicar",
        items: "artículos",
        noResults: "No se encontraron artículos",
        resetFilters: "Borrar filtros",
        min: "min",
        cart: "Carrito",
        empty: "Tu carrito está vacío",
        emptyDesc: "Agrega artículos del menú",
        browseMenu: "Ver Menú",
        orderNoteLabel: "Solicitudes especiales",
        orderNotePlaceholder: "Alergias, preferencias, etc...",
        subtotalLabel: "Subtotal",
        taxNotice: "Impuestos y tarifas calculados al pagar",
        checkout: "Pagar",
        customize: "Personalizar",
        addOnsTitle: "Toppings y extras perfectos",
        addOnsHelper: "Toca para agregar toppings y extras a este platillo.",
        noAddOns: "Este artículo aún no cuenta con toppings adicionales.",
        requiredLabel: "Obligatorio",
        optionalLabel: "Opcional",
        chooseOne: "Elige uno",
        chooseUpTo: "Elige hasta {{count}}",
        chooseAtLeast: "Elige al menos {{count}}",
        chooseBetween: "Elige entre {{min}} y {{max}}",
        maxSelectionReached: "Has alcanzado el máximo de opciones",
        selectionRequirementTitle: "Agrega los toppings requeridos",
        selectionRequirement: "Selecciona al menos {{count}} opción(es) en {{group}}",
        selectedCount: "{{count}} seleccionado(s)",
        included: "Incluido",
        quantity: "Cantidad",
        specialInstructions: "Solicitudes especiales",
        placeholder: "Alergias, preferencias...",
        add: "Agregar",
        optional: "Opcional",
        fulfillmentLabel: "Cumplimiento",
        toGo: "Para llevar",
        dineIn: "Comer aquí",
        delivery: "Entrega",
        guests: "Comensales",
        guestsHint: "¿Cuántos asientos?",
        nameLabel: "Nombre",
        phoneLabel: "Teléfono",
        emailLabel: "Correo",
        updatePhone: "Actualizar teléfono",
        textUpdatesHint: "Te enviaremos actualizaciones por mensaje de texto.",
        emailHint: "Enviaremos actualizaciones y recibos si compartes tu correo.",
        preferences: "Preferencias",
        saveFavorites: "Recordar mis favoritos y preferencias en este dispositivo.",
        saveFavoritesHint: "Solo guardaremos esto cuando des tu consentimiento.",
        loyaltyUpdates: "Enviarme actualizaciones de crédito de lealtad y recompensas.",
        loyaltyHint: "Solo usaremos tu información para rastrear puntos para este lugar.",
        loyaltyBalanceLabel: "Saldo de lealtad",
        loyaltyRedeemLabel: "Usar puntos en este pedido",
        loyaltyRedeemHint: "Puedes canjear hasta {{points}} pts hoy.",
        emailOrderUpdates: "Enviarme actualizaciones de pedidos por correo electrónico.",
        emailOrderHint: "Agrega una dirección de correo para recibir actualizaciones.",
        textOrderUpdates: "Enviarme actualizaciones de pedidos por mensaje de texto.",
        textHint: "Pueden aplicar tarifas de mensajes y datos.",
        scheduleOrder: "Autorizo programar este pedido para recoger más tarde.",
        scheduleHint: "Habilita la programación y elige un horario que te funcione.",
        readyWindow: "Ventana preferida",
        asap: "Lo antes posible",
        in30: "En 30 minutos",
        in60: "En 1 hora",
        in90: "En 1.5 horas",
        orderSummary: "Resumen del pedido",
        itemLabel: "artículo",
        payment: "Método de pago",
        applePay: "Apple Pay",
        googlePay: "Google Pay",
        cardPay: "Tarjeta de crédito/débito",
        cash: "Efectivo",
        comingSoon: "(próximamente)",
        summary: "Resumen",
        tip: "Propina",
        tipSuggestion: "¡Gran servicio!",
        customTipLabel: "Personalizado",
        discountLabel: "Descuento",
        estimatedTaxes: "Impuestos estimados",
        total: "Total",
        placeOrder: "Realizar Pedido",
        promoCodeLabel: "Código promocional",
        apply: "Aplicar",
        remove: "Quitar",
        gutCheck: "¿Necesitas una verificación rápida?",
        secureCheckout: "Pago seguro",
        orderAccuracy: "Consejos de precisión del pedido",
        orderAccuracyText:
          "¿Necesitas ajustar algo? Toca cualquier artículo en tu carrito para ajustar modificadores o notas antes de enviar.",
        prepTimeline: "Cronograma de preparación",
        orderConfirmed: "¡Pedido Confirmado!",
        orderNumberLabel: "Número de Pedido",
        orderPlaced: "Pedido realizado a las",
        estimatedReady: "Tiempo estimado de preparación",
        contactInfo: "Información de Contacto",
        fulfillmentType: "Tipo de Servicio",
        dineInLabel: "Comer Aquí",
        toGoLabel: "Para Llevar",
        deliveryLabel: "Entrega",
        trackOrder: "Rastrear Pedido",
        backToMenu: "Volver al Menú",
        thankYou: "¡Gracias por tu pedido!",
        accessibility: "Accesibilidad",
        accessibilityDescription: "Ajusta las preferencias de visualización para este menú.",
        accessibilitySaveLabel: "Guardar configuraciones de confort",
        accessibilitySaveDescription: "Usaremos estas preferencias en este dispositivo.",
        accessibilitySaveButton: "Guardar preferencias",
        accessibilitySavedToast: "Preferencias guardadas",
        accessibilityLivePreview: "Vista previa en vivo",
        textSizeLabel: "Tamaño del Texto",
        textSizeHelper: "Elige el tamaño de lectura que te resulte cómodo.",
        standard: "Estándar",
        large: "Grande",
        textSizeStandardDescription: "Equilibrado para explorar todo el menú.",
        textSizeLargeDescription: "Etiquetas más grandes y espaciadas para mayor claridad.",
        comfortSettingsTitle: "Mejoras de confort",
        comfortSettingsDescription: "Calma el movimiento y resalta los contornos cuando necesitas enfoque.",
        reduceMotion: "Reducir movimiento",
        reduceMotionDescription: "Suaviza animaciones repetitivas y transiciones deslizantes.",
        highContrastLabel: "Contornos de enfoque de alto contraste",
        highContrastDescription: "Resalta los anillos de enfoque para navegar con teclado.",
      },
    }),
    [],
  );

  const t = content[language];

  const toggleFavorite = (itemId: string) => {
    const currentFavorites = guestProfile?.favorites || [];
    const newFavorites = currentFavorites.includes(itemId)
      ? currentFavorites.filter((id) => id !== itemId)
      : [...currentFavorites, itemId];

    const newProfile = { ...(guestProfile || { favorites: [] }), favorites: newFavorites };
    setGuestProfile(newProfile);
  };

  const recommendedItems = useMemo(() => {
    if (recommendations?.items?.length) {
      return recommendations.items;
    }
    return items.filter((item) => item.popular).slice(0, 3);
  }, [items, recommendations]);

  const itemNameLookup = useMemo(() => {
    const map = new Map<string, string>();
    items.forEach((item) => {
      map.set(item.id, item.name);
    });
    return map;
  }, [items]);

  const recommendedItemIds = useMemo(() => {
    return new Set(recommendations?.items?.map((item) => item.id) ?? []);
  }, [recommendations]);

  const attachRecommendedItemIds = useMemo(() => {
    return new Set(attachRecommendations?.items?.map((entry) => entry.item.id) ?? []);
  }, [attachRecommendations]);

  const emitRecommendationAction = useCallback(
    (itemId: string, action: RecommendationActionType) => {
      if (recommendedItemIds.has(itemId)) {
        recommendations?.onAction?.(itemId, action);
      }
      if (attachRecommendedItemIds.has(itemId)) {
        attachRecommendations?.onAction?.(itemId, action);
      }
    },
    [attachRecommendations?.onAction, attachRecommendedItemIds, recommendedItemIds, recommendations?.onAction],
  );

  const categoryOptions = useMemo(() => {
    const options: CategoryOption[] = [];
    const seen = new Set<string>();

    const pushOption = (option: CategoryOption) => {
      if (seen.has(option.id)) return;
      seen.add(option.id);
      options.push(option);
    };

    pushOption({ id: "all", name: t.allDiet });

    const hasPopularItems = items.some((item) => item.popular);
    if (hasPopularItems || selectedCategory === "popular") {
      pushOption({ id: "popular", name: t.popularCategory });
    }

    categories.forEach((category) => {
      if (!category.id) {
        return;
      }
      const label =
        typeof category.name === "string" ? category.name.trim() : "";
      pushOption({
        id: category.id,
        name: label.length > 0 ? label : category.id,
      });
    });

    return options;
  }, [categories, items, selectedCategory, t]);

  const filteredItems = useMemo(() => {
    let filtered = [...items];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query),
      );
    }

    if (selectedCategory !== "all") {
      if (selectedCategory === "popular") {
        filtered = filtered.filter((item) => item.popular);
      } else {
        filtered = filtered.filter((item) => item.categoryId === selectedCategory);
      }
    }

    if (dietFilter !== "all") {
      filtered = filtered.filter((item) => item.dietary.includes(dietFilter));
    }

    if (showFavoritesOnly && guestProfile?.favorites) {
      filtered = filtered.filter((item) => guestProfile.favorites.includes(item.id));
    }

    const sorter = [...filtered];
    if (sortBy === "price-asc") {
      sorter.sort((a, b) => a.price - b.price);
    } else if (sortBy === "price-desc") {
      sorter.sort((a, b) => b.price - a.price);
    } else if (sortBy === "prep-time") {
      sorter.sort((a, b) => a.prepTime - b.prepTime);
    } else if (sortBy === "featured") {
      sorter.sort((a, b) => {
        const aFav = guestProfile?.favorites?.includes(a.id) ? 1 : 0;
        const bFav = guestProfile?.favorites?.includes(b.id) ? 1 : 0;
        if (aFav !== bFav) return bFav - aFav;

        const aPop = a.popular ? 1 : 0;
        const bPop = b.popular ? 1 : 0;
        return bPop - aPop;
      });
    }

    return sorter;
  }, [dietFilter, guestProfile, items, searchQuery, selectedCategory, showFavoritesOnly, sortBy]);

  const itemsByCategory = useMemo(() => {
    const grouped = new Map<string, { id: string; name: string; items: MenuItem[] }>();
    filteredItems.forEach((item) => {
      const key = item.categoryId || "uncategorized";
      const name =
        item.categoryName || (language === "es" ? "Sin categoría" : "Uncategorized");
      if (!grouped.has(key)) {
        grouped.set(key, { id: key, name, items: [] });
      }
      grouped.get(key)!.items.push(item);
    });
    return Array.from(grouped.values());
  }, [filteredItems, language]);

  const hasActiveFilters = Boolean(searchQuery || selectedCategory !== "all" || dietFilter !== "all" || showFavoritesOnly);
  const attachRailItems = attachRecommendations?.items ?? [];
  const showAttachRail = !hasActiveFilters && attachRailItems.length > 0;
  const filterCount = [selectedCategory !== "all", dietFilter !== "all", showFavoritesOnly, sortBy !== "featured"].filter(
    Boolean,
  ).length;

  const tipOptions = [15, 18, 20, 25];
  const tipAmount = customTip ? parseFloat(customTip) || 0 : (subtotal * tipPercentage) / 100;
  const discountAmount = promoApplied ? discount : 0;
  const afterDiscount = subtotal - discountAmount;
  const tax = afterDiscount * 0.0;
  const total = afterDiscount + tax + tipAmount;

  const handleApplyPromo = () => {
    const validCodes: Record<string, number> = {
      SAVE10: subtotal * 0.1,
      FIVE: 5,
    };

    const key = promoCode.toUpperCase();
    if (validCodes[key]) {
      setDiscount(validCodes[key]);
      setPromoApplied(true);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode("");
    setPromoApplied(false);
    setDiscount(0);
  };

  const handleItemClick = (item: MenuItem) => {
    emitRecommendationAction(item.id, "open_customization");
    setCustomizationQuantity(1);
    setCustomizationNotes("");
    setSelectedItem(item);
    setCurrentView("item-customization");
  };

  const handleQuickAdd = (item: MenuItem) => {
    onAddToCart(item, 1, [], "");
    emitRecommendationAction(item.id, "add_item");
    toast.success(`${item.name} added`);
  };

  const handleModifierSelection = useCallback(
    (group: MenuModifierGroup, modifierId: string) => {
      const groupName = group.isFallback ? t.addOnsTitle : group.name;
      setSelectedModifiers((previous) => {
        const groupModifierIds = new Set(group.modifiers.map((modifier) => modifier.id));
        const groupSelections = previous.filter((id) => groupModifierIds.has(id));
        const isSelected = previous.includes(modifierId);

        if (isSelected) {
          return previous.filter((id) => id !== modifierId);
        }

        if (group.selectionMode === "single" || (group.maxSelections ?? null) === 1) {
          return [...previous.filter((id) => !groupModifierIds.has(id)), modifierId];
        }

        if (typeof group.maxSelections === "number" && groupSelections.length >= group.maxSelections) {
          toast({
            variant: "destructive",
            title: t.maxSelectionReached,
            description: groupName,
          });
          return previous;
        }

        return [...previous, modifierId];
      });
    },
    [toast, t.addOnsTitle, t.maxSelectionReached],
  );

  const getSelectionHint = useCallback(
    (group: MenuModifierGroup) => {
      if (group.helperText && group.helperText.trim().length > 0) {
        return group.helperText;
      }
      if (group.selectionMode === "single" || (group.maxSelections ?? null) === 1) {
        return t.chooseOne;
      }
      if (group.minSelections > 0 && typeof group.maxSelections === "number") {
        return formatTemplate(t.chooseBetween, { min: group.minSelections, max: group.maxSelections });
      }
      if (typeof group.maxSelections === "number") {
        return formatTemplate(t.chooseUpTo, { count: group.maxSelections });
      }
      if (group.minSelections > 0) {
        return formatTemplate(t.chooseAtLeast, { count: group.minSelections });
      }
      return t.optionalLabel;
    },
    [t],
  );

  const handleAddFromCustomization = () => {
    if (!selectedItem) {
      return;
    }
    const unmetGroup = selectedItem.modifierGroups.find((group) => {
      if (group.minSelections <= 0) {
        return false;
      }
      const groupSelectionCount = selectedModifiers.filter((id) =>
        group.modifiers.some((modifier) => modifier.id === id),
      ).length;
      return groupSelectionCount < group.minSelections;
    });
    if (unmetGroup) {
      toast({
        variant: "destructive",
        title: t.selectionRequirementTitle,
        description: formatTemplate(t.selectionRequirement, {
          count: unmetGroup.minSelections,
          group: unmetGroup.isFallback ? t.addOnsTitle : unmetGroup.name,
        }),
      });
      return;
    }

    onAddToCart(selectedItem, customizationQuantity, selectedModifiers, customizationNotes);
    emitRecommendationAction(selectedItem.id, "add_item");
    toast.success(`${selectedItem.name} added`);
    setCurrentView("menu");
    setSelectedItem(null);
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      toast.error(t.emptyDesc);
      return;
    }

    setIsSubmitting(true);
    try {
      let redeemPoints = 0;
      if (loyaltyProgram && loyaltyProgram.status === "enabled" && loyaltyProgram.redemption?.enabled) {
        const parsed = Number(loyaltyRedeemInput.trim());
        if (Number.isFinite(parsed) && parsed > 0) {
          redeemPoints = Math.max(0, Math.floor(parsed));
          if (maxRedeemablePoints > 0) {
            redeemPoints = Math.min(redeemPoints, maxRedeemablePoints);
          }
        }
      }

      const result = await onPlaceOrder({
        fulfillment,
        guests: Math.max(1, parseInt(guests, 10) || 1),
        name,
        email,
        paymentMethod,
        tipAmount,
        discountAmount,
        promoCode: promoApplied ? promoCode : null,
        scheduleOrder,
        readyWindow,
        savePreferences,
        loyaltyUpdates,
        emailUpdates,
        textUpdates,
        orderNote,
        loyaltyRedeemPoints: redeemPoints,
      });

      setOrderNumber(result.orderNumber);
      setOrderTime(result.placedAt);
      setEstimatedReadyTime(result.estimatedReady ?? "");
      setConfirmationCart(result.cart);
      setConfirmationTotals(result.totals);
      setCurrentView("confirmation");
      setPromoCode("");
      setPromoApplied(false);
      setDiscount(0);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to place order. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedModifierObjects =
    selectedItem?.modifiers?.filter((modifier) => selectedModifiers.includes(modifier.id)) ?? [];
  const modifierCost = selectedModifierObjects.reduce((sum, modifier) => sum + modifier.priceDelta, 0);

  const customizationTotalPrice = selectedItem
    ? (selectedItem.price + modifierCost) * customizationQuantity
    : 0;

  const confirmationItems = confirmationCart.length > 0 ? confirmationCart : cart;
  const confirmationSummaryTotals = confirmationTotals ?? {
    subtotal,
    tax,
    tip: tipAmount,
    discount: discountAmount,
    total,
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const savedTextSize = localStorage.getItem("text_size") as TextSize;
    const savedReduceMotion = localStorage.getItem("reduce_motion") === "true";
    const savedHighContrast = localStorage.getItem("high_contrast") === "true";

    if (savedTextSize) setTextSize(savedTextSize);
    if (savedReduceMotion) setReduceMotion(savedReduceMotion);
    if (savedHighContrast) setHighContrast(savedHighContrast);

    applyAccessibilityPreferences(savedTextSize || "base", savedReduceMotion, savedHighContrast);
  }, []);

  const applyAccessibilityPreferences = (size: TextSize, motionValue: boolean, contrastValue: boolean) => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.classList.toggle("text-large", size === "large");
    root.classList.toggle("reduce-motion", motionValue);
    root.classList.toggle("high-contrast", contrastValue);
  };

  const handleTextSizeChange = (value: TextSize) => {
    setTextSize(value);
    if (typeof window !== "undefined") {
      localStorage.setItem("text_size", value);
    }
    applyAccessibilityPreferences(value, reduceMotion, highContrast);
  };

  const handleReduceMotionChange = (checked: boolean) => {
    setReduceMotion(checked);
    if (typeof window !== "undefined") {
      localStorage.setItem("reduce_motion", checked.toString());
    }
    applyAccessibilityPreferences(textSize, checked, highContrast);
  };

  const handleHighContrastChange = (checked: boolean) => {
    setHighContrast(checked);
    if (typeof window !== "undefined") {
      localStorage.setItem("high_contrast", checked.toString());
    }
    applyAccessibilityPreferences(textSize, reduceMotion, checked);
  };

  const handleSaveAccessibilityPreferences = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem("text_size", textSize);
      localStorage.setItem("reduce_motion", reduceMotion.toString());
      localStorage.setItem("high_contrast", highContrast.toString());
    }
    applyAccessibilityPreferences(textSize, reduceMotion, highContrast);
    toast.success(t.accessibilitySavedToast);
    setAccessibilityOpen(false);
  };

  const maxRedeemablePoints = useMemo(() => {
    if (!loyaltyBalance || !Number.isFinite(loyaltyBalance)) {
      return 0;
    }
    const value = Math.floor(loyaltyBalance);
    return value > 0 ? value : 0;
  }, [loyaltyBalance]);

  const renderItemCard = (item: MenuItem, isFavorite: boolean) => {
    const isRecommended = recommendedItemIds.has(item.id);
    if (viewMode === "list") {
      return (
        <div
          onClick={() => {
            handleItemClick(item);
          }}
          className="flex cursor-pointer overflow-hidden rounded-xl border border-zinc-200 bg-white transition-all hover:shadow-lg active:scale-[0.99]"
        >
          <div className="relative h-24 w-24 shrink-0 bg-zinc-100">
            <ImageWithFallback src={item.image} alt={item.name} className="h-full w-full object-cover" />
            {isRecommended && (
              <Badge className="absolute right-1 top-1 h-4 bg-purple-600/90 px-1.5 text-[10px] text-white">
                {t.recommended}
              </Badge>
            )}
            {item.popular && (
              <Badge className="absolute left-1 top-1 h-4 bg-zinc-900/80 px-1.5 text-xs text-white">Popular</Badge>
            )}
          </div>

          <div className="flex flex-1 flex-col justify-between p-3">
            <div>
              <div className="mb-1 flex items-start justify-between gap-2">
                <h3 className="line-clamp-1 text-sm leading-snug">{item.name}</h3>
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleFavorite(item.id);
                  }}
                  className="shrink-0 p-1"
                >
                  <Heart className={cn("h-4 w-4", isFavorite ? "fill-red-500 text-red-500" : "text-zinc-400")} />
                </button>
              </div>
              <p className="line-clamp-1 text-xs text-zinc-500">{item.description}</p>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-zinc-900">${item.price.toFixed(2)}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-500">
                  {item.prepTime}
                  {t.min}
                </span>
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    if (item.modifiers && item.modifiers.length > 0) {
                      handleItemClick(item);
                    } else {
                      handleQuickAdd(item);
                    }
                  }}
                  className="rounded-full bg-zinc-900 p-1.5 text-white transition-all hover:scale-110 hover:bg-zinc-800 active:scale-95"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        onClick={() => {
          handleItemClick(item);
        }}
        className="cursor-pointer overflow-hidden rounded-xl border border-zinc-200 bg-white transition-all hover:shadow-lg active:scale-[0.98]"
      >
        <div className="relative aspect-[4/3] bg-zinc-100">
          <ImageWithFallback src={item.image} alt={item.name} className="h-full w-full object-cover" />
          {isRecommended && (
            <Badge className="absolute left-2 top-2 z-10 bg-purple-600/90 px-2 py-0 text-[10px] text-white">
              {t.recommended}
            </Badge>
          )}

          <button
            onClick={(event) => {
              event.stopPropagation();
              toggleFavorite(item.id);
            }}
            className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 shadow-sm transition-colors hover:bg-white"
          >
            <Heart className={cn("h-4 w-4", isFavorite ? "fill-red-500 text-red-500" : "text-zinc-400")} />
          </button>

          {item.popular && (
            <Badge
              className={cn(
                "absolute bg-zinc-900/80 px-2 text-xs text-white",
                isRecommended ? "left-2 top-10 h-5" : "left-2 top-2 h-5",
              )}
            >
              Popular
            </Badge>
          )}

          <button
            onClick={(event) => {
              event.stopPropagation();
              if (item.modifiers && item.modifiers.length > 0) {
                handleItemClick(item);
              } else {
                handleQuickAdd(item);
              }
            }}
            className="absolute bottom-2 right-2 rounded-full bg-zinc-900 p-2 text-white shadow-lg transition-all hover:scale-110 hover:bg-zinc-800 active:scale-95"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        <div className="p-3">
          <div className="mb-1 flex items-start justify-between gap-1">
            <h3 className="line-clamp-1 text-sm leading-snug">{item.name}</h3>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-zinc-900">${item.price.toFixed(2)}</span>
            <span className="text-xs text-zinc-500">
              {item.prepTime}
              {t.min}
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (currentView === "menu") {
    return (
      <div className="min-h-screen bg-zinc-50 pb-24">
        <header className="sticky top-0 z-40 border-b border-zinc-200 bg-white/95 px-3 py-3 backdrop-blur-sm">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="truncate text-lg">{venue.name}</h1>
                  <Sheet>
                    <SheetTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-lg transition-colors hover:bg-zinc-100"
                        aria-label="View venue details"
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    </SheetTrigger>
                    <SheetContent side="right" className="!w-full sm:!max-w-md">
                      <SheetHeader>
                        <SheetTitle>{venue.name}</SheetTitle>
                        <SheetDescription>Restaurant details</SheetDescription>
                      </SheetHeader>
                      <div className="mt-6 space-y-4">
                        {(typeof venue.rating === "number" || typeof venue.reviewCount === "number") && (
                          <div className="flex items-center gap-3 text-sm">
                            <Star className="h-5 w-5 fill-zinc-900 text-zinc-900" />
                            <span>
                              {typeof venue.rating === "number" ? venue.rating.toFixed(1) : "-"}
                              {typeof venue.reviewCount === "number" ? ` • ${venue.reviewCount} reviews` : ""}
                            </span>
                          </div>
                        )}
                        {venue.prepTime && (
                          <div className="flex items-center gap-3 text-sm">
                            <Clock className="h-5 w-5 text-zinc-600" />
                            <span>{venue.prepTime} avg wait</span>
                          </div>
                        )}
                        <div className="text-sm text-zinc-600">
                          {venue.location && <p>{venue.location}</p>}
                          <p>
                            {t.table} {venue.tableNumber ?? "-"}
                          </p>
                        </div>
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  {venue.tableNumber && (
                    <span>
                      {t.table} {venue.tableNumber}
                    </span>
                  )}
                  {typeof venue.rating === "number" && (
                    <>
                      <span>•</span>
                      <Star className="h-3 w-3 fill-zinc-400 text-zinc-400" />
                      <span>{venue.rating.toFixed(1)}</span>
                    </>
                  )}
                </div>
              </div>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={() => setCurrentView("cart")}
                  size="icon"
                  className="relative h-11 w-11 shrink-0 rounded-full bg-zinc-900 hover:bg-zinc-800"
                >
                  <ShoppingCart className="h-5 w-5" />
                  <AnimatePresence>
                    {cartItemCount > 0 && (
                      <motion.span
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 400, damping: 15 }}
                        className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white"
                      >
                        <motion.span
                          key={cartItemCount}
                          initial={{ scale: 1.5, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ type: "spring" }}
                        >
                          {cartItemCount}
                        </motion.span>
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              </motion.div>
            </div>

            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                <Input
                  placeholder={t.search}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="h-10 bg-zinc-50 pl-9"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2">
                    <X className="h-4 w-4 text-zinc-400" />
                  </button>
                )}
              </div>

              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0"
                onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
              >
                {viewMode === "grid" ? <List className="h-4 w-4" /> : <Grid3x3 className="h-4 w-4" />}
              </Button>

              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0"
                onClick={() => {
                  const nextLanguage = language === "en" ? "es" : "en";
                  setLanguage(nextLanguage);
                  if (typeof window !== "undefined") {
                    localStorage.setItem("language", nextLanguage);
                  }
                }}
              >
                <Languages className="h-4 w-4" />
              </Button>

              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0"
                onClick={() => onUpdatePhone(phoneNumber)}
                aria-label={t.updatePhone}
                title={t.updatePhone}
              >
                <Smartphone className="h-4 w-4" />
              </Button>

              <Sheet open={accessibilityOpen} onOpenChange={setAccessibilityOpen}>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    aria-label={t.accessibility}
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-zinc-200 bg-white transition-colors hover:bg-zinc-50"
                  >
                    <Settings className="h-4 w-4" />
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="flex h-full max-h-[90vh] !w-full flex-col overflow-hidden rounded-l-[32px] border border-emerald-100/70 bg-gradient-to-b from-white via-emerald-50/70 to-white px-0 pt-0 shadow-[0_25px_60px_rgba(16,185,129,0.25)] sm:!max-w-lg"
                >
                  <SheetHeader className="border-b border-emerald-100/70 bg-gradient-to-br from-white to-emerald-50/70 px-6 py-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 shadow-inner shadow-white/40">
                          <Settings className="h-5 w-5" />
                        </div>
                        <div>
                          <SheetTitle className="text-lg font-semibold text-emerald-950">{t.accessibility}</SheetTitle>
                          <SheetDescription className="text-sm text-emerald-700">
                            {t.accessibilityDescription}
                          </SheetDescription>
                        </div>
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100/80 bg-white/80 px-3 py-1 text-xs font-semibold text-emerald-700">
                        <Sparkles className="h-3.5 w-3.5 text-emerald-500" />
                        {t.accessibilityLivePreview}
                      </div>
                    </div>
                  </SheetHeader>

                  <div className="flex-1 overflow-y-auto px-6 py-6">
                    <div className="space-y-5">
                      <section className="rounded-3xl border border-emerald-100/80 bg-white/95 p-5 shadow-lg shadow-emerald-500/5">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-emerald-900">{t.textSizeLabel}</p>
                            <p className="text-xs text-emerald-600">{t.textSizeHelper}</p>
                          </div>
                          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                            {textSize === "large" ? t.large : t.standard}
                          </span>
                        </div>
                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                          {[
                            {
                              id: "text-base",
                              value: "base" as TextSize,
                              label: t.standard,
                              description: t.textSizeStandardDescription,
                            },
                            {
                              id: "text-large",
                              value: "large" as TextSize,
                              label: t.large,
                              description: t.textSizeLargeDescription,
                            },
                          ].map((option) => (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => handleTextSizeChange(option.value)}
                              className={cn(
                                "flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
                                textSize === option.value
                                  ? "border-emerald-200 bg-emerald-50/70 shadow-sm shadow-emerald-500/10"
                                  : "border-emerald-100 bg-white hover:border-emerald-200",
                              )}
                            >
                              <div>
                                <p className="text-sm font-semibold text-emerald-900">{option.label}</p>
                                <p className="text-xs text-emerald-600">{option.description}</p>
                              </div>
                              <RadioGroupItem
                                id={option.id}
                                checked={textSize === option.value}
                                onChange={() => handleTextSizeChange(option.value)}
                              />
                            </button>
                          ))}
                        </div>
                      </section>

                      <section className="rounded-3xl border border-emerald-100/80 bg-white/95 p-5 shadow-lg shadow-emerald-500/5">
                        <p className="text-sm font-semibold text-emerald-900">{t.comfortSettingsTitle}</p>
                        <p className="text-xs text-emerald-600">{t.comfortSettingsDescription}</p>

                        <div className="mt-4 space-y-3">
                          <label
                            htmlFor="reduce-motion"
                            className="flex items-start gap-3 rounded-2xl border border-emerald-50 bg-emerald-50/60 px-4 py-3"
                          >
                            <div className="mt-0.5">
                              <Checkbox
                                id="reduce-motion"
                                checked={reduceMotion}
                                onCheckedChange={handleReduceMotionChange}
                              />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-emerald-900">{t.reduceMotion}</p>
                              <p className="text-xs text-emerald-600">{t.reduceMotionDescription}</p>
                            </div>
                          </label>

                          <label
                            htmlFor="high-contrast"
                            className="flex items-start gap-3 rounded-2xl border border-zinc-900/10 bg-zinc-900/95 px-4 py-3 text-white shadow-inner shadow-black/30"
                          >
                            <div className="mt-0.5">
                              <Checkbox
                                id="high-contrast"
                                checked={highContrast}
                                onCheckedChange={handleHighContrastChange}
                              />
                            </div>
                            <div>
                              <p className="text-sm font-semibold">{t.highContrastLabel}</p>
                              <p className="text-xs text-zinc-200">{t.highContrastDescription}</p>
                            </div>
                          </label>
                        </div>
                      </section>
                      <div className="flex flex-col gap-3 rounded-3xl border border-emerald-100/80 bg-white/95 p-5 shadow-sm shadow-emerald-500/5 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-emerald-900">{t.accessibilitySaveLabel}</p>
                          <p className="text-xs text-emerald-600">{t.accessibilitySaveDescription}</p>
                        </div>
                        <Button
                          type="button"
                          className="h-11 w-full rounded-2xl bg-emerald-600 text-white shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-700 sm:w-auto"
                          onClick={handleSaveAccessibilityPreferences}
                        >
                          {t.accessibilitySaveButton}
                        </Button>
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    aria-label={t.sort}
                    className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-zinc-200 bg-white transition-colors hover:bg-zinc-50"
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    {filterCount > 0 && (
                      <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-zinc-900 text-xs text-white">
                        {filterCount}
                      </span>
                    )}
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="bottom"
                  className="max-h-[80vh] w-full max-w-full overflow-y-auto border-t border-zinc-200 px-5 pb-8 pt-6 sm:px-6"
                >
                  <SheetHeader>
                    <SheetTitle>{t.sort}</SheetTitle>
                    <SheetDescription>Customize your menu view</SheetDescription>
                  </SheetHeader>

                  <div className="mt-6 space-y-6">
                    <div>
                      <h4 className="mb-3 text-sm">Sort By</h4>
                      <div className="space-y-2">
                        {[
                          { value: "featured" as SortOption, label: t.sortFeatured },
                          { value: "price-asc" as SortOption, label: t.sortPriceAsc },
                          { value: "price-desc" as SortOption, label: t.sortPriceDesc },
                          { value: "prep-time" as SortOption, label: t.sortPrep },
                        ].map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setSortBy(option.value)}
                            className={cn(
                              "flex w-full items-center justify-between rounded-lg p-3 text-left transition-colors",
                              sortBy === option.value ? "bg-zinc-100" : "hover:bg-zinc-50",
                            )}
                          >
                            <span className="text-sm">{option.label}</span>
                            {sortBy === option.value && <Check className="h-4 w-4" />}
                          </button>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h4 className="mb-3 text-sm">Dietary Preferences</h4>
                      <div className="flex gap-2 rounded-xl bg-zinc-100 p-1">
                        <button
                          onClick={() => setDietFilter("all")}
                          className={cn(
                            "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs transition-all",
                            dietFilter === "all" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-600 hover:text-zinc-900",
                          )}
                        >
                          <Grid3x3 className="h-3.5 w-3.5" />
                          {t.allDiet}
                        </button>
                        <button
                          onClick={() => setDietFilter("vegetarian")}
                          className={cn(
                            "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs transition-all",
                            dietFilter === "vegetarian"
                              ? "bg-white text-zinc-900 shadow-sm"
                              : "text-zinc-600 hover:text-zinc-900",
                          )}
                        >
                          <Leaf className="h-3.5 w-3.5" />
                          {t.vegetarian}
                        </button>
                        <button
                          onClick={() => setDietFilter("vegan")}
                          className={cn(
                            "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs transition-all",
                            dietFilter === "vegan"
                              ? "bg-white text-zinc-900 shadow-sm"
                              : "text-zinc-600 hover:text-zinc-900",
                          )}
                        >
                          <Wheat className="h-3.5 w-3.5" />
                          {t.vegan}
                        </button>
                      </div>
                    </div>

                    <Separator />

                    <button
                      onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                      className={cn(
                        "flex w-full items-center justify-between rounded-lg p-3 transition-colors",
                        showFavoritesOnly ? "bg-zinc-100" : "hover:bg-zinc-50",
                      )}
                    >
                      <span className="flex items-center gap-2 text-sm">
                        <Heart
                          className={cn(
                            "h-4 w-4",
                            showFavoritesOnly ? "fill-red-500 text-red-500" : "text-zinc-600",
                          )}
                        />
                        {t.favorites}
                      </span>
                      {showFavoritesOnly && <Check className="h-4 w-4" />}
                    </button>

                    {filterCount > 0 && (
                      <>
                        <Separator />
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            setSelectedCategory("all");
                            setDietFilter("all");
                            setShowFavoritesOnly(false);
                            setSortBy("featured");
                            setFiltersOpen(false);
                          }}
                        >
                          {t.clearFilters}
                        </Button>
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            <div className="-mx-3 mt-3 flex gap-1.5 overflow-x-auto px-3 pb-1 scrollbar-hide">
              {categoryOptions.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category.id)}
                  className="h-8 shrink-0 px-3 text-xs"
                >
                  {category.name}
                </Button>
              ))}
            </div>
          </div>
        </header>

        <div className="space-y-6 px-3 py-4">
          <AnimatePresence>
            {!hasActiveFilters && recommendedItems.length > 0 && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="mb-3 flex items-center gap-2"
                >
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Sparkles className="h-4 w-4 text-purple-600" />
                  </motion.div>
                  <h2 className="text-base">{t.recommended}</h2>
                </motion.div>

                <div className={viewMode === "grid" ? "grid grid-cols-2 gap-3" : "space-y-3"}>
                  {recommendedItems.slice(0, 2).map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.3 }}
                    >
                      {renderItemCard(item, Boolean(guestProfile?.favorites?.includes(item.id)))}
                    </motion.div>
                  ))}
                </div>
              </motion.section>
            )}
          </AnimatePresence>

          {showAttachRail && (
            <section className="rounded-2xl border border-emerald-100 bg-gradient-to-r from-emerald-50 via-white to-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
                    {t.attachSubheading}
                  </p>
                  <h2 className="text-base text-emerald-900">{t.attachHeading}</h2>
                </div>
              </div>
              <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1 scrollbar-hide">
                {attachRailItems.map((entry) => {
                  const attachRate = `${Math.round(entry.probability * 100)}% ${t.attachRateSuffix}`;
                  const matchNames = entry.matches
                    .map((match) => itemNameLookup.get(match.mainItemId))
                    .filter((name): name is string => Boolean(name))
                    .slice(0, 2);
                  return (
                    <button
                      key={entry.item.id}
                      onClick={() => handleItemClick(entry.item)}
                      className="w-60 shrink-0 rounded-xl border border-emerald-100 bg-white/90 p-3 text-left shadow-[0_3px_10px_rgba(16,185,129,0.12)] transition hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(16,185,129,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-zinc-900">{entry.item.name}</p>
                          <p className="text-xs text-emerald-600">{attachRate}</p>
                        </div>
                        <span className="text-sm font-semibold text-emerald-700">${entry.item.price.toFixed(2)}</span>
                      </div>
                      {matchNames.length > 0 && (
                        <p className="mt-2 text-xs text-zinc-500">
                          {t.attachMatchesLabel} {matchNames.join(", ")}
                        </p>
                      )}
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <span className="text-xs text-zinc-500">{entry.item.prepTime} {t.min}</span>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleQuickAdd(entry.item);
                          }}
                        >
                          {t.attachAction}
                        </Button>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <AnimatePresence mode="popLayout">
            {itemsByCategory.map((categoryGroup, sectionIndex) => (
              <motion.section
                key={categoryGroup.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: sectionIndex * 0.1, duration: 0.3 }}
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: sectionIndex * 0.1 + 0.1 }}
                  className="mb-3 flex items-center justify-between"
                >
                  <h2 className="text-base capitalize">{categoryGroup.name}</h2>
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: sectionIndex * 0.1 + 0.2 }}
                    className="rounded-full bg-zinc-100 px-2 py-1 text-xs text-zinc-500"
                  >
                    {categoryGroup.items.length}
                  </motion.span>
                </motion.div>

                <div className={viewMode === "grid" ? "grid grid-cols-2 gap-3" : "space-y-3"}>
                  {categoryGroup.items.map((item, itemIndex) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{
                        delay: sectionIndex * 0.1 + itemIndex * 0.05,
                        duration: 0.2,
                        type: "spring",
                        stiffness: 200,
                        damping: 20,
                      }}
                    >
                      {renderItemCard(item, Boolean(guestProfile?.favorites?.includes(item.id)))}
                    </motion.div>
                  ))}
                </div>
              </motion.section>
            ))}
          </AnimatePresence>

          {filteredItems.length === 0 && (
            <div className="py-16 text-center">
              <p className="mb-4 text-zinc-500">{t.noResults}</p>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("all");
                    setDietFilter("all");
                    setShowFavoritesOnly(false);
                    setSortBy("featured");
                  }}
                >
                  {t.resetFilters}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (currentView === "item-customization" && selectedItem) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-3 py-6 sm:py-10">
        <div
          className="absolute inset-0"
          aria-hidden="true"
          onClick={() => setCurrentView("menu")}
        />
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 28 }}
          className="relative z-10 flex w-full max-w-2xl flex-col overflow-hidden rounded-[32px] bg-white shadow-2xl shadow-emerald-950/10 max-h-[90vh]"
        >
          <button
            onClick={() => setCurrentView("menu")}
            aria-label={t.backToMenu}
            className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 bg-white/90 text-zinc-700 transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex-1 overflow-y-auto px-5 pb-5 pt-12 sm:px-8 sm:pb-8">
            <div className="space-y-5">
              <div className="rounded-[28px] border border-zinc-100 bg-white shadow-sm shadow-zinc-900/5">
                <div className="relative aspect-[16/9] overflow-hidden rounded-t-[28px] bg-zinc-100">
                  <ImageWithFallback
                    src={selectedItem.image}
                    alt={selectedItem.name}
                    className="h-full w-full object-cover"
                  />
                </div>

                <div className="space-y-3 px-6 py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-zinc-900">{selectedItem.name}</h2>
                    </div>
                    <span className="shrink-0 text-lg font-semibold text-zinc-900">
                      ${selectedItem.price.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-600">{selectedItem.description}</p>
                </div>
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-zinc-900">{t.customize}</h3>
                  <span className="text-xs text-zinc-500">{t.addOnsHelper}</span>
                </div>
                {selectedItem.modifierGroups.length > 0 ? (
                  <div className="space-y-4">
                    {selectedItem.modifierGroups.map((group) => {
                      const displayName = group.isFallback ? t.addOnsTitle : group.name;
                      const helper = group.isFallback ? t.addOnsHelper : getSelectionHint(group);
                      const groupModifierIds = new Set(group.modifiers.map((modifier) => modifier.id));
                      const selectedCount = selectedModifiers.filter((id) => groupModifierIds.has(id)).length;
                      return (
                        <div
                          key={group.id}
                          className="rounded-3xl border border-zinc-100/80 bg-white/95 p-4 shadow-sm shadow-zinc-900/5"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-zinc-900">{displayName}</p>
                              <p className="text-xs text-zinc-500">
                                {helper}
                                {selectedCount > 0 &&
                                  ` • ${formatTemplate(t.selectedCount, { count: selectedCount })}`}
                              </p>
                            </div>
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                              {group.isRequired || group.minSelections > 0 ? t.requiredLabel : t.optionalLabel}
                            </span>
                          </div>

                          <div className="mt-3 space-y-2">
                            {group.modifiers.map((modifier) => {
                              const isSelected = selectedModifiers.includes(modifier.id);
                              const priceText =
                                modifier.priceDelta > 0 ? `+$${modifier.priceDelta.toFixed(2)}` : t.included;
                              return (
                                <button
                                  key={modifier.id}
                                  onClick={() => handleModifierSelection(group, modifier.id)}
                                  className={cn(
                                    "flex w-full items-center justify-between rounded-2xl border p-3 text-left transition-colors",
                                    isSelected
                                      ? "border-zinc-900 bg-zinc-50 shadow-sm"
                                      : "border-zinc-200 hover:bg-zinc-50",
                                  )}
                                >
                                  <div>
                                    <p className="text-sm font-medium text-zinc-900">{modifier.name}</p>
                                    {modifier.isDefault && <p className="text-xs text-emerald-600">{t.included}</p>}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                                    <span>{priceText}</span>
                                    {isSelected && <Check className="h-4 w-4 text-emerald-600" />}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-3xl border border-dashed border-zinc-200 bg-zinc-50/80 p-4 text-sm text-zinc-500">
                    {t.noAddOns}
                  </div>
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-zinc-900">{t.specialInstructions}</h3>
                  <span className="text-xs text-zinc-400">{t.optional}</span>
                </div>
                <Textarea
                  value={customizationNotes}
                  onChange={(event) => setCustomizationNotes(event.target.value.slice(0, 200))}
                  placeholder={t.placeholder}
                  className="min-h-[72px] rounded-2xl text-sm"
                  maxLength={200}
                />
                <p className="mt-1.5 text-right text-xs text-zinc-400">{customizationNotes.length}/200</p>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-zinc-900">{t.quantity}</h3>
                <div className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-zinc-50/60 p-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCustomizationQuantity(Math.max(1, customizationQuantity - 1))}
                    disabled={customizationQuantity <= 1}
                    className="h-11 w-11 rounded-xl"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-16 text-center text-lg font-semibold text-zinc-900">{customizationQuantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCustomizationQuantity(customizationQuantity + 1)}
                    className="h-11 w-11 rounded-xl"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-200 bg-white/95 px-5 py-4 sm:px-8">
            <Button onClick={handleAddFromCustomization} className="h-12 w-full bg-zinc-900 hover:bg-zinc-800">
              {t.add} • ${customizationTotalPrice.toFixed(2)}
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (currentView === "cart") {
    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

    if (cart.length === 0) {
      return (
        <div className="flex min-h-screen flex-col bg-zinc-50">
          <header className="border-b border-zinc-200 bg-white px-3 py-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => setCurrentView("menu")} className="h-9 w-9">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-lg">{t.cart}</h1>
            </div>
          </header>

          <div className="flex flex-1 items-center justify-center px-4">
            <div className="max-w-sm space-y-4 text-center">
              <div className="mb-2 inline-flex h-16 w-16 items-center justify-center rounded-full bg-zinc-100">
                <ShoppingBag className="h-8 w-8 text-zinc-400" />
              </div>
              <h2>{t.empty}</h2>
              <p className="text-sm text-zinc-600">{t.emptyDesc}</p>
              <Button onClick={() => setCurrentView("menu")} className="bg-zinc-900 hover:bg-zinc-800">
                {t.browseMenu}
              </Button>
            </div>
          </div>
      </div>
    );
  }

    return (
      <div className="min-h-screen bg-zinc-50 pb-28">
        <header className="sticky top-0 z-40 border-b border-zinc-200 bg-white px-3 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => setCurrentView("menu")} className="h-9 w-9">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-lg">{t.cart}</h1>
                <p className="text-xs text-zinc-500">
                  {totalItems} {t.items}
                </p>
              </div>
            </div>
          </div>
        </header>

        <div className="space-y-4 px-3 py-4">
          <div className="space-y-3">
            {cart.map((cartItem, index) => {
              const modifiersCost = cartItem.modifiers.reduce((sum, modifier) => sum + modifier.priceDelta, 0);
              const lineTotal = (cartItem.item.price + modifiersCost) * cartItem.quantity;

              return (
                <div key={`${cartItem.item.id}-${index}`} className="rounded-xl border border-zinc-200 bg-white p-3">
                  <div className="flex gap-3">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-zinc-100">
                      <ImageWithFallback
                        src={cartItem.item.image}
                        alt={cartItem.item.name}
                        className="h-full w-full object-cover"
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="line-clamp-1 text-sm">{cartItem.item.name}</h3>
                          {cartItem.modifiers.length > 0 && (
                            <p className="line-clamp-1 text-xs text-zinc-500">
                              {cartItem.modifiers.map((modifier) => modifier.name).join(", ")}
                            </p>
                          )}
                          {cartItem.notes && (
                            <p className="mt-0.5 line-clamp-1 text-xs italic text-zinc-500">"{cartItem.notes}"</p>
                          )}
                        </div>
                        <button
                          onClick={() => onRemoveFromCart(index)}
                          className="shrink-0 rounded-full p-1 transition-colors hover:bg-zinc-100"
                        >
                          <X className="h-4 w-4 text-zinc-400" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => onUpdateCartItem(index, cartItem.quantity - 1)}
                            className="h-7 w-7"
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-6 text-center text-sm">{cartItem.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => onUpdateCartItem(index, cartItem.quantity + 1)}
                            className="h-7 w-7"
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <span className="text-sm">${lineTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border border-zinc-200 bg-white p-3">
            <h3 className="mb-2 text-sm">{t.orderNoteLabel}</h3>
            <Textarea
              value={orderNote}
              onChange={(event) => onOrderNoteChange(event.target.value.slice(0, 300))}
              placeholder={t.orderNotePlaceholder}
              className="min-h-[60px] text-sm"
              maxLength={300}
            />
            <p className="mt-1.5 text-right text-xs text-zinc-400">{orderNote.length}/300</p>
          </div>

          <div className="rounded-xl border border-zinc-200 bg-white p-3">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-sm text-zinc-600">{t.subtotalLabel}</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <p className="text-xs text-zinc-500">{t.taxNotice}</p>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 border-t border-zinc-200 bg-white p-4">
          <Button
            onClick={() => setCurrentView("checkout")}
            className="h-12 w-full bg-zinc-900 hover:bg-zinc-800"
          >
            {t.checkout} • ${subtotal.toFixed(2)}
          </Button>
        </div>
      </div>
    );
  }

  if (currentView === "checkout") {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen bg-zinc-50 pb-28"
      >
        <motion.header
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="sticky top-0 z-40 border-b border-zinc-200 bg-white/95 px-3 py-3 backdrop-blur-sm"
        >
          <div className="flex items-center gap-3">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button variant="ghost" size="icon" onClick={() => setCurrentView("cart")} className="h-9 w-9">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </motion.div>
            <h1 className="text-lg">{t.checkout}</h1>
          </div>
        </motion.header>

        <div className="space-y-4 px-3 py-4">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 20 }}
            className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
          >
            <h2 className="mb-3 text-base">{t.fulfillmentLabel}</h2>
            <div className="grid grid-cols-3 gap-2">
              {(["to-go", "dine-in", "delivery"] as const).map((type) => (
                <motion.button
                  key={type}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setFulfillment(type)}
                  className={cn(
                    "relative rounded-lg border-2 px-4 py-3 text-sm transition-all",
                    fulfillment === type
                      ? "border-zinc-900 bg-zinc-50 shadow-md"
                      : "border-zinc-200 hover:border-zinc-300 hover:shadow-sm",
                  )}
                >
                  {type === "to-go" ? t.toGo : type === "dine-in" ? t.dineIn : t.delivery}
                  <AnimatePresence>
                    {fulfillment === type && (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0, rotate: 180 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                        className="absolute right-2 top-2"
                      >
                        <Check className="h-4 w-4" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 20 }}
            className="space-y-4 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
          >
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-base">{t.guests}</h2>
                <p className="text-xs text-zinc-500">{t.guestsHint}</p>
              </div>
              <div className="flex items-center justify-end">
                <Input
                  type="number"
                  value={guests}
                  onChange={(event) => setGuests(event.target.value)}
                  min={1}
                  className="h-10 w-20 text-center"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-900">{t.nameLabel}</Label>
                <Input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder={t.optional}
                  className="mt-1 h-10"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-900">{t.phoneLabel}</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input value={phoneNumber} disabled className="h-10 flex-1 bg-zinc-50" />
                  <button
                    onClick={() => onUpdatePhone(phoneNumber)}
                    className="whitespace-nowrap text-xs text-blue-600 hover:text-blue-700"
                  >
                    {t.updatePhone}
                  </button>
                </div>
              </div>
            </div>
            <p className="text-xs text-zinc-500">{t.textUpdatesHint}</p>

            <div>
              <Label className="text-xs text-zinc-900">{t.emailLabel}</Label>
              <div className="relative mt-1">
                <Input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t.optional}
                  className="h-10 pr-10"
                />
                <motion.div
                  animate={email ? { scale: [1, 1.2, 1] } : {}}
                  transition={{ duration: 0.3 }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <Mail className="h-4 w-4 text-teal-600" />
                </motion.div>
              </div>
              <p className="mt-1 text-xs text-zinc-500">{t.emailHint}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 20 }}
            className="space-y-4 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
          >
            <h2 className="text-base">{t.preferences}</h2>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox id="save-favorites" checked={savePreferences} onCheckedChange={setSavePreferences} />
                <div className="flex-1">
                  <label htmlFor="save-favorites" className="block cursor-pointer text-sm">
                    {t.saveFavorites}
                  </label>
                  <p className="mt-0.5 text-xs text-zinc-500">{t.saveFavoritesHint}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox id="loyalty-updates" checked={loyaltyUpdates} onCheckedChange={setLoyaltyUpdates} />
                <div className="flex-1">
                  <label htmlFor="loyalty-updates" className="block cursor-pointer text-sm">
                    {t.loyaltyUpdates}
                  </label>
                  <p className="mt-0.5 text-xs text-zinc-500">{t.loyaltyHint}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox id="email-updates" checked={emailUpdates} onCheckedChange={setEmailUpdates} />
                <div className="flex-1">
                  <label htmlFor="email-updates" className="block cursor-pointer text-sm">
                    {t.emailOrderUpdates}
                  </label>
                  <p className="mt-0.5 text-xs text-zinc-500">{t.emailOrderHint}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox id="text-updates" checked={textUpdates} onCheckedChange={setTextUpdates} />
                <div className="flex-1">
                  <label htmlFor="text-updates" className="block cursor-pointer text-sm">
                    {t.textOrderUpdates}
                  </label>
                  <p className="mt-0.5 text-xs text-zinc-500">{t.textHint}</p>
                </div>
              </div>

              {loyaltyProgram &&
              loyaltyProgram.status === "enabled" &&
              loyaltyProgram.redemption?.enabled &&
              maxRedeemablePoints > 0 ? (
                <div className="mt-2 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-zinc-900">{t.loyaltyBalanceLabel}</span>
                    <span className="font-semibold text-teal-700">
                      {new Intl.NumberFormat(language === "es" ? "es-MX" : "en-US").format(maxRedeemablePoints)}
                    </span>
                  </div>
                  <div className="mt-2">
                    <Label className="text-xs text-zinc-900" htmlFor="loyalty-redeem">
                      {t.loyaltyRedeemLabel}
                    </Label>
                    <Input
                      id="loyalty-redeem"
                      inputMode="numeric"
                      value={loyaltyRedeemInput}
                      onChange={(event) =>
                        setLoyaltyRedeemInput(event.target.value.replace(/[^0-9]/g, ""))
                      }
                      placeholder="0"
                      className="mt-1 h-9"
                    />
                    <p className="mt-1 text-xs text-zinc-500">
                      {formatTemplate(t.loyaltyRedeemHint, { points: maxRedeemablePoints })}
                    </p>
                  </div>
                </div>
              ) : null}

              <Separator />

              <div className="flex items-start gap-3">
                <Checkbox id="schedule-order" checked={scheduleOrder} onCheckedChange={setScheduleOrder} />
                <div className="flex-1">
                  <label htmlFor="schedule-order" className="block cursor-pointer text-sm">
                    {t.scheduleOrder}
                  </label>
                  <p className="mt-0.5 text-xs text-zinc-500">{t.scheduleHint}</p>
                </div>
              </div>

              {scheduleOrder && (
                <div>
                  <Label className="text-xs text-zinc-900">{t.readyWindow}</Label>
                  <Select value={readyWindow} onValueChange={setReadyWindow}>
                    <SelectTrigger className="mt-1 h-10">
                      <SelectValue value={readyWindow} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asap">{t.asap}</SelectItem>
                      <SelectItem value="30min">{t.in30}</SelectItem>
                      <SelectItem value="60min">{t.in60}</SelectItem>
                      <SelectItem value="90min">{t.in90}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25, type: "spring", stiffness: 200, damping: 20 }}
          >
            <Collapsible open={orderSummaryOpen} onOpenChange={setOrderSummaryOpen}>
              <CollapsibleTrigger asChild>
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="flex w-full items-center justify-between rounded-xl border border-zinc-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex items-center gap-2">
                    <h2 className="text-base">{t.orderSummary}</h2>
                    <Badge variant="secondary" className="text-xs">
                      {cart.length} {cart.length === 1 ? t.itemLabel : t.items}
                    </Badge>
                  </div>
                  <motion.div animate={{ rotate: orderSummaryOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                    <ChevronDown className="h-5 w-5" />
                  </motion.div>
                </motion.button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden rounded-b-xl border-x border-b border-zinc-200 bg-white px-4 pb-4"
                >
                  {cart.map((item, index) => {
                    const modifiersCost = item.modifiers.reduce((sum, modifier) => sum + modifier.priceDelta, 0);
                    const lineTotal = (item.item.price + modifiersCost) * item.quantity;
                    return (
                      <motion.div
                        key={item.item.id + index.toString()}
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-center justify-between py-2 text-sm"
                      >
                        <span className="flex-1">
                          {item.item.name} x{item.quantity}
                        </span>
                        <span>${lineTotal.toFixed(2)}</span>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </CollapsibleContent>
            </Collapsible>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200, damping: 20 }}
          >
            <Collapsible open={promoOpen} onOpenChange={setPromoOpen}>
              <CollapsibleTrigger asChild>
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="flex w-full items-center justify-between rounded-xl border border-zinc-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                >
                  <h2 className="text-base">{t.promoCodeLabel}</h2>
                  <motion.div animate={{ rotate: promoOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                    <ChevronDown className="h-5 w-5" />
                  </motion.div>
                </motion.button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden rounded-b-xl border-x border-b border-zinc-200 bg-white px-4 pb-4"
                >
                  <AnimatePresence mode="wait">
                    {!promoApplied ? (
                      <motion.div
                        key="promo-input"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="flex gap-2"
                      >
                        <Input
                          value={promoCode}
                          onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
                          placeholder="SAVE10"
                          className="h-10 flex-1"
                        />
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button onClick={handleApplyPromo} variant="outline" size="sm">
                            {t.apply}
                          </Button>
                        </motion.div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="promo-applied"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-3"
                      >
                        <div className="flex items-center gap-2">
                          <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          >
                            <Check className="h-4 w-4 text-green-700" />
                          </motion.div>
                          <div>
                            <p className="text-sm">{promoCode}</p>
                            <p className="text-xs text-green-700">-${discountAmount.toFixed(2)}</p>
                          </div>
                        </div>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button onClick={handleRemovePromo} variant="ghost" size="sm" className="h-8 text-xs">
                            {t.remove}
                          </Button>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </CollapsibleContent>
            </Collapsible>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.35, type: "spring", stiffness: 200, damping: 20 }}
            className="space-y-4 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
          >
            <h2 className="text-base">{t.tip}</h2>

            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4, type: "spring" }}
              className="relative overflow-hidden rounded-lg border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100/50 p-4"
            >
              <motion.div
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              />
              <div className="relative flex items-start gap-3">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="flex shrink-0 items-center justify-center rounded-full bg-purple-600 p-2"
                >
                  <Sparkles className="h-4 w-4 text-white" />
                </motion.div>
                <div className="flex-1">
                  <p className="text-sm leading-relaxed text-purple-950">
                    <span className="mb-1 block">{t.tipSuggestion}</span>
                    <span className="text-xs text-purple-700">Your server went above and beyond today.</span>
                  </p>
                </div>
              </div>
            </motion.div>

            <div className="grid grid-cols-4 gap-2">
              {tipOptions.map((percentage, index) => (
                <motion.div
                  key={percentage}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.45 + index * 0.05, type: "spring" }}
                >
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant={tipPercentage === percentage && !customTip ? "default" : "outline"}
                      onClick={() => {
                        setTipPercentage(percentage);
                        setCustomTip("");
                      }}
                      className="h-10 w-full text-sm"
                    >
                      {percentage}%
                    </Button>
                  </motion.div>
                </motion.div>
              ))}
            </div>

            <div>
              <Label className="text-xs text-zinc-600">{t.customTipLabel}</Label>
              <Input
                type="number"
                value={customTip}
                onChange={(event) => setCustomTip(event.target.value)}
                placeholder="0.00"
                className="mt-1 h-10"
                step="0.01"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, type: "spring", stiffness: 200, damping: 20 }}
            className="space-y-2 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
          >
            <div className="flex justify-between text-sm text-zinc-600">
              <span>{t.subtotalLabel}</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>

            <div className="flex justify-between text-sm text-zinc-600">
              <span>{t.estimatedTaxes}</span>
              <span>${tax.toFixed(2)}</span>
            </div>

            <div className="flex justify-between text-sm text-zinc-600">
              <span>{t.tip}</span>
              <span>${tipAmount.toFixed(2)}</span>
            </div>

            <Separator />

            <div className="flex justify-between pt-1">
              <span>{t.total}</span>
              <motion.span
                key={total}
                initial={{ scale: 1.2, color: "#22c55e" }}
                animate={{ scale: 1, color: "#000000" }}
                transition={{ duration: 0.3 }}
              >
                ${total.toFixed(2)}
              </motion.span>
            </div>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.55, type: "spring", stiffness: 200, damping: 20 }}
            className="space-y-2"
          >
            <h2 className="px-1 text-base">{t.gutCheck}</h2>

            <Collapsible open={secureCheckoutOpen} onOpenChange={setSecureCheckoutOpen}>
              <CollapsibleTrigger asChild>
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="flex w-full items-center justify-between rounded-xl border border-zinc-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-zinc-600" />
                    <span className="text-sm">{t.secureCheckout}</span>
                  </div>
                  <motion.div animate={{ rotate: secureCheckoutOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                    <ChevronDown className="h-5 w-5" />
                  </motion.div>
                </motion.button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden rounded-b-xl border-x border-b border-zinc-200 bg-white px-4 pb-4"
                >
                  <p className="text-sm text-zinc-600">
                    Your payment information is encrypted and secure.
                  </p>
                </motion.div>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible open={orderAccuracyOpen} onOpenChange={setOrderAccuracyOpen}>
              <CollapsibleTrigger asChild>
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="flex w-full items-center justify-between rounded-xl border border-zinc-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-zinc-600" />
                    <span className="text-sm">{t.orderAccuracy}</span>
                  </div>
                  <motion.div animate={{ rotate: orderAccuracyOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                    <ChevronDown className="h-5 w-5" />
                  </motion.div>
                </motion.button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden rounded-b-xl border-x border-b border-zinc-200 bg-white px-4 pb-4"
                >
                  <p className="text-sm text-zinc-600">{t.orderAccuracyText}</p>
                </motion.div>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible open={prepTimelineOpen} onOpenChange={setPrepTimelineOpen}>
              <CollapsibleTrigger asChild>
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="flex w-full items-center justify-between rounded-xl border border-zinc-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-zinc-600" />
                    <span className="text-sm">{t.prepTimeline}</span>
                  </div>
                  <motion.div animate={{ rotate: prepTimelineOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                    <ChevronDown className="h-5 w-5" />
                  </motion.div>
                </motion.button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden rounded-b-xl border-x border-b border-zinc-200 bg-white px-4 pb-4"
                >
                  <p className="text-sm text-zinc-600">Your order will be ready in approximately 15-20 minutes.</p>
                </motion.div>
              </CollapsibleContent>
            </Collapsible>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, type: "spring", stiffness: 200, damping: 20 }}
            className="space-y-2 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
          >
            <h2 className="mb-2 text-base">{t.payment}</h2>

            <motion.button
              whileHover={{ scale: 0.99 }}
              disabled
              className="flex w-full cursor-not-allowed items-center justify-between rounded-lg border border-zinc-200 px-4 py-4 opacity-50"
            >
              <div className="flex items-center gap-3">
                <Smartphone className="h-5 w-5" />
                <span className="text-sm">
                  {t.applePay} {t.comingSoon}
                </span>
              </div>
            </motion.button>

            <motion.button
              whileHover={{ scale: 0.99 }}
              disabled
              className="flex w-full cursor-not-allowed items-center justify-between rounded-lg border border-zinc-200 px-4 py-4 opacity-50"
            >
              <div className="flex items-center gap-3">
                <Smartphone className="h-5 w-5" />
                <span className="text-sm">
                  {t.googlePay} {t.comingSoon}
                </span>
              </div>
            </motion.button>

            <motion.button
              whileHover={{ scale: 0.99 }}
              disabled
              className="flex w-full cursor-not-allowed items-center justify-between rounded-lg border border-zinc-200 px-4 py-4 opacity-50"
            >
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5" />
                <span className="text-sm">
                  {t.cardPay} {t.comingSoon}
                </span>
              </div>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => setPaymentMethod("cash")}
              className={cn(
                "flex w-full items-center justify-between rounded-lg border-2 px-4 py-4 transition-all",
                paymentMethod === "cash"
                  ? "border-zinc-900 bg-zinc-50 shadow-md"
                  : "border-zinc-200 hover:border-zinc-300 hover:shadow-sm",
              )}
            >
              <div className="flex items-center gap-3">
                <Banknote className="h-5 w-5" />
                <span className="text-sm">{t.cash}</span>
              </div>
              <AnimatePresence>
                {paymentMethod === "cash" && (
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    exit={{ scale: 0, rotate: 180 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Check className="h-5 w-5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </motion.div>
        </div>

        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200, damping: 20 }}
          className="fixed bottom-0 left-0 right-0 border-t border-zinc-200 bg-white/95 p-4 backdrop-blur-sm shadow-2xl"
        >
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              onClick={handlePlaceOrder}
              disabled={isSubmitting}
              className="relative h-12 w-full overflow-hidden bg-gradient-to-r from-zinc-900 to-zinc-800 shadow-lg hover:from-zinc-800 hover:to-zinc-700"
            >
              <motion.div
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
              />
              <span className="relative z-10">
                {t.placeOrder} • ${total.toFixed(2)}
              </span>
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }

  if (currentView === "confirmation") {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen bg-zinc-50"
      >
        <header className="sticky top-0 z-50 border-b border-zinc-200 bg-white/95 px-4 py-4 backdrop-blur-sm">
          <div className="mx-auto flex max-w-md items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-900">
                <span className="text-white">🍽️</span>
              </div>
              <div>
                <h1>{venue.name}</h1>
                <p className="text-sm text-zinc-500">
                  {t.table} {venue.tableNumber ?? "-"}
                </p>
              </div>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-md px-4 py-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="mb-8 flex flex-col items-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 20 }}
              className="mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-green-600 shadow-xl"
            >
              <Check className="h-12 w-12 text-white" strokeWidth={3} />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-2 text-center"
            >
              {t.orderConfirmed}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-center text-zinc-600"
            >
              {t.thankYou}
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-4 grid gap-4 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">{t.orderNumberLabel}</span>
              <span className="font-mono">{orderNumber}</span>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">{t.orderPlaced}</span>
              <span>{orderTime}</span>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">{t.estimatedReady}</span>
              <span className="text-green-600">{estimatedReadyTime}</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-4 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm"
          >
            <h3 className="mb-4">{t.fulfillmentType}</h3>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100">
                {fulfillment === "dine-in" && <span>🍽️</span>}
                {fulfillment === "to-go" && <ShoppingBag className="h-5 w-5 text-zinc-600" />}
                {fulfillment === "delivery" && <span>🚗</span>}
              </div>
              <div>
                <p>
                  {fulfillment === "dine-in"
                    ? t.dineInLabel
                    : fulfillment === "to-go"
                    ? t.toGoLabel
                    : t.deliveryLabel}
                </p>
                {fulfillment === "dine-in" && (
                  <p className="text-sm text-zinc-500">
                    {guests} {guests === "1" ? "guest" : "guests"}
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mb-4 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm"
          >
            <h3 className="mb-4">{t.orderSummary}</h3>

            <div className="mb-4 space-y-3">
              {confirmationItems.map((item, index) => (
                <div key={item.item.id + index.toString()} className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-2">
                      <span className="text-zinc-500">{item.quantity}x</span>
                      <div>
                        <p>{item.item.name}</p>
                        {item.modifiers.length > 0 && (
                          <p className="text-sm text-zinc-500">
                            {item.modifiers.map((modifier) => modifier.name).join(", ")}
                          </p>
                        )}
                        {item.notes && (
                          <p className="text-sm italic text-zinc-500">Note: {item.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <span>
                    $
                    {(
                      (item.item.price +
                        item.modifiers.reduce((sum, modifier) => sum + modifier.priceDelta, 0)) *
                      item.quantity
                    ).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            <Separator className="my-4" />

            <div className="space-y-2">
              <div className="flex justify-between text-zinc-600">
                <span>{t.subtotalLabel}</span>
                <span>${confirmationSummaryTotals.subtotal.toFixed(2)}</span>
              </div>
              {confirmationSummaryTotals.discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t.discountLabel}</span>
                  <span>-${confirmationSummaryTotals.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-zinc-600">
                <span>{t.tip}</span>
                <span>${confirmationSummaryTotals.tip.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-zinc-600">
                <span>{t.estimatedTaxes}</span>
                <span>${confirmationSummaryTotals.tax.toFixed(2)}</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between">
                <span>{t.total}</span>
                <span>${confirmationSummaryTotals.total.toFixed(2)}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mb-4 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm"
          >
            <h3 className="mb-4">{t.contactInfo}</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Smartphone className="h-5 w-5 text-zinc-400" />
                <span>{phoneNumber}</span>
              </div>
              {name && (
                <div className="flex items-center gap-3">
                  <span className="h-5 w-5 text-zinc-400">👤</span>
                  <span>{name}</span>
                </div>
              )}
              {email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-zinc-400" />
                  <span>{email}</span>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mb-20 grid grid-cols-2 gap-3"
          >
            <Button
              variant="outline"
              onClick={() => {
                setCurrentView("menu");
                setName("");
                setEmail("");
                setGuests("1");
                setCustomizationNotes("");
                setPromoCode("");
                setPromoApplied(false);
                setDiscount(0);
              }}
              className="h-12"
            >
              {t.backToMenu}
            </Button>
            <Button
              onClick={() => {
                alert(`Track order ${orderNumber}`);
              }}
              className="h-12 bg-zinc-900 hover:bg-zinc-800"
            >
              <Clock className="mr-2 h-4 w-4" />
              {t.trackOrder}
            </Button>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  return null;
}
interface AssistantMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  badgeLabel?: string | null;
  source?: "instant" | "assistant";
}

interface AssistantSuggestion {
  title: string;
  description: string;
  prompt: string;
}

interface AssistantInsight {
  label: string;
  value: string;
  description: string;
}

interface AssistantStory {
  title: string;
  body: string;
  attribution: string;
}

interface AssistantPromise {
  title: string;
  body: string;
  bullets: string[];
}

interface MarketingPanelProps {
  heroSubtitle: string;
  insightsTitle: string;
  insights: AssistantInsight[];
  story: AssistantStory;
  promise: AssistantPromise;
  className?: string;
}

const AssistantGlyph = React.memo(function AssistantGlyph() {
  return (
    <motion.div
      aria-hidden="true"
      animate={{ rotate: [0, -4, 4, 0] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      className="relative flex h-12 w-12 items-center justify-center"
    >
      <span className="absolute inset-0 rounded-[1.65rem] bg-white/20 opacity-70 blur-2xl" />
      <motion.span
        className="absolute -top-1.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.8)]"
        animate={{ y: [0, -3, 0], opacity: [0.9, 1, 0.9] }}
        transition={{ duration: 3.2, repeat: Infinity }}
      />
      <motion.span
        className="absolute -bottom-1.5 -left-1 h-2.5 w-2.5 rounded-full bg-cyan-300/90 shadow-[0_0_10px_rgba(103,232,249,0.8)]"
        animate={{ y: [0, 3, 0], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 3.2, repeat: Infinity, delay: 0.5 }}
      />
      <span className="relative flex h-10 w-10 items-center justify-center rounded-[1.25rem] bg-white/15 backdrop-blur">
        <span className="absolute inset-[2px] rounded-[1.1rem] bg-gradient-to-br from-emerald-200/60 via-emerald-100/30 to-transparent opacity-80" />
        <span className="absolute inset-[6px] rounded-[0.95rem] bg-gradient-to-br from-emerald-500/40 via-emerald-400/40 to-emerald-300/30 blur-[1px]" />
        <BrainCircuit className="relative h-5 w-5 text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.3)]" />
      </span>
      <Sparkles className="absolute -top-3 left-0 h-4 w-4 text-white/70 drop-shadow-[0_0_6px_rgba(255,255,255,0.5)]" />
    </motion.div>
  );
});

const MarketingPanel = React.memo(function MarketingPanel({
  heroSubtitle,
  insightsTitle,
  insights,
  story,
  promise,
  className = "",
}: MarketingPanelProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn("flex flex-col gap-4", className)}
    >
      <div className="rounded-3xl border border-emerald-100 bg-white/90 p-5 shadow-lg shadow-emerald-900/5 backdrop-blur">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-emerald-500">{insightsTitle}</p>
            <p className="mt-1 text-sm text-zinc-500">{heroSubtitle}</p>
          </div>
          <motion.span
            animate={{ rotate: [0, 6, -6, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600"
          >
            <Sparkles className="h-4 w-4" />
          </motion.span>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {insights.map((insight) => (
            <div
              key={`${insight.label}-${insight.value}`}
              className="rounded-2xl border border-emerald-50 bg-white/80 p-4 shadow-sm"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-500">{insight.label}</p>
              <p className="mt-1 text-2xl font-semibold text-zinc-900">{insight.value}</p>
              <p className="mt-1 text-xs text-zinc-500">{insight.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-600 to-emerald-500 p-5 text-white shadow-xl shadow-emerald-900/20">
        <p className="text-xs uppercase tracking-[0.3em] text-emerald-50">{story.title}</p>
        <p className="mt-3 text-sm leading-relaxed text-emerald-50/90">{story.body}</p>
        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-white/80">{story.attribution}</p>
      </div>
      <div className="rounded-3xl border border-zinc-200 bg-white/90 p-5 shadow-lg shadow-emerald-900/5 backdrop-blur">
        <p className="text-sm font-semibold text-zinc-900">{promise.title}</p>
        <p className="mt-2 text-sm text-zinc-600">{promise.body}</p>
        <ul className="mt-4 space-y-2 text-sm text-zinc-700">
          {promise.bullets.map((bullet) => (
            <li key={bullet} className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
});

interface AIAssistantPageProps {
  language: "en" | "es";
  initialMessages?: AssistantMessage[];
  initialConversationId?: string | null;
  onMessagesChange?: (messages: AssistantMessage[], conversationId: string | null) => void;
  onSendMessage: (
    message: string,
    conversationId: string | null,
    options?: { intentId?: string | null }
  ) => Promise<{
    reply: string;
    conversationId: string | null;
    quotaStatus?: string | null;
    quotaMessage?: string | null;
    resolutionTier?: ResolutionTier | null;
  }>;
  disabled?: boolean;
  disabledReason?: string | null;
  resolveInstantAnswer?: (
    message: string,
    conversationId: string | null,
  ) => { reply: string; badgeLabel?: string; intentId?: string | null } | null;
}

function AIAssistantPage({
  language,
  initialMessages = [],
  initialConversationId = null,
  onMessagesChange,
  onSendMessage,
  disabled = false,
  disabledReason,
  resolveInstantAnswer,
}: AIAssistantPageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<AssistantMessage[]>(() => initialMessages);
  const [conversationId, setConversationId] = useState<string | null>(initialConversationId);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusOverride, setStatusOverride] = useState<string | null>(null);
  const [lastResolutionLabel, setLastResolutionLabel] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMessages(initialMessages);
  }, [initialMessages]);

  useEffect(() => {
    setConversationId(initialConversationId);
  }, [initialConversationId]);

  useEffect(() => {
    onMessagesChange?.(messages, conversationId);
  }, [messages, conversationId, onMessagesChange]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const { t } = useTranslation("public");
  const heroCopy = useMemo(() => {
    const fallback = {
      badge: "Dining room AI host",
      title: "Braisey",
      subtitle: "A calm concierge that keeps every guest informed without leaving the floor.",
      uptime: "Serving 17 tables tonight",
      metricLabel: "Average check lift",
      metricValue: "+18%",
      suggestionIntro: "Guide the conversation",
    };
    return {
      badge: t("aiAssistant.heroBadge", { defaultValue: fallback.badge }),
      title: t("aiAssistant.heroTitle", { defaultValue: fallback.title }),
      subtitle: t("aiAssistant.heroSubtitle", { defaultValue: fallback.subtitle }),
      uptime: t("aiAssistant.heroUptime", { defaultValue: fallback.uptime }),
      metricLabel: t("aiAssistant.heroMetricLabel", { defaultValue: fallback.metricLabel }),
      metricValue: t("aiAssistant.heroMetricValue", { defaultValue: fallback.metricValue }),
      suggestionIntro: t("aiAssistant.suggestionIntro", { defaultValue: fallback.suggestionIntro }),
    };
  }, [t]);
  const assistantSuggestions = useMemo(() => {
    const fallback: AssistantSuggestion[] = [
      {
        title: "Pair the evening special",
        description: "Let Braisey suggest a wine flight.",
        prompt: "Which wine pairing works best with the cedar-plank salmon special?",
      },
      {
        title: "Clarify wait times",
        description: "Reassure guests without leaving the table.",
        prompt: "How long until the mains reach Table 8? Let them know politely.",
      },
      {
        title: "Highlight a dessert",
        description: "Serve a story, not just calories.",
        prompt: "Recommend a light dessert that follows a spicy entrée.",
      },
    ];
    const localized = t("aiAssistant.suggestions", {
      returnObjects: true,
      defaultValue: fallback,
    }) as AssistantSuggestion[];
    if (!Array.isArray(localized) || localized.length === 0) {
      return fallback;
    }
    return localized;
  }, [t]);
  const marketingInsights = useMemo(() => {
    const fallback: AssistantInsight[] = [
      {
        label: "Tables guided each week",
        value: "1,482",
        description: "Guests stay seated because answers arrive instantly.",
      },
      {
        label: "Average upsell lift",
        value: "+18%",
        description: "Personalized pairings suggested after entrées are placed.",
      },
    ];
    const localized = t("aiAssistant.marketing.insights", {
      returnObjects: true,
      defaultValue: fallback,
    }) as AssistantInsight[];
    if (!Array.isArray(localized) || localized.length === 0) {
      return fallback;
    }
    return localized;
  }, [t]);
  const marketingInsightsTitle = t("aiAssistant.marketing.insightsTitle", {
    defaultValue: "Why guests love Braisey",
  });
  const marketingStory = useMemo(
    () => ({
      title: t("aiAssistant.marketing.storyTitle", { defaultValue: "Tonight's highlight" }),
      body: t("aiAssistant.marketing.storyBody", {
        defaultValue:
          "Table 14 committed to the cedar-plank salmon flight after Braisey previewed the chef's tasting notes.",
      }),
      attribution: t("aiAssistant.marketing.storyAttribution", { defaultValue: "Floor lead, Bella Vista" }),
    }),
    [t],
  );
  const marketingPromise = useMemo(() => {
    const fallbackBullets = [
      "Menu stories grounded in your latest inventory.",
      "Real-time pacing so guests know what's next.",
    ];
    const bulletList = t("aiAssistant.marketing.promiseBullets", {
      returnObjects: true,
      defaultValue: fallbackBullets,
    }) as string[];
    return {
      title: t("aiAssistant.marketing.promiseTitle", { defaultValue: "Designed for calm service" }),
      body: t("aiAssistant.marketing.promiseBody", {
        defaultValue:
          "Braisey blends your menu, pacing, and guest mood into confident replies-without pulling a teammate away.",
      }),
      bullets:
        Array.isArray(bulletList) && bulletList.length > 0
          ? bulletList.filter((bullet) => typeof bullet === "string" && bullet.trim().length > 0)
          : fallbackBullets,
    };
  }, [t]);
  const placeholder = t("aiAssistant.placeholder", { defaultValue: "Ask anything..." });
  const emptyState = t("aiAssistant.emptyState", {
    defaultValue: "Need a recommendation or have a question? Start a conversation and I will help you out.",
  });
  const fallbackMessageCopy = t("aiAssistant.fallback", {
    defaultValue: "I'm still checking on that. I can't reach the team from here, so please flag someone if you need help right away.",
  });
  const disabledCopy = disabledReason ?? t("aiAssistant.errorUnavailable", {
    defaultValue: "The assistant is unavailable right now. Please flag a team member.",
  });
  const sendAria = t("aiAssistant.sendAria", { defaultValue: "Send assistant message" });
  const sendButtonLabel = t("aiAssistant.sendButton", { defaultValue: "Send" });
  const guestLabel = t("aiAssistant.guestLabel", { defaultValue: "You" });
  const assistantLabel = t("aiAssistant.assistantLabel", { defaultValue: "Braisey" });
  const statusLabel = t("aiAssistant.operationalStatus", { defaultValue: "Status" });
  const statusValue = disabled
    ? disabledCopy
    : statusOverride ??
      t("aiAssistant.statusOk", { defaultValue: "All systems operational" });
  const assistantInstantBadge = t("assistant.instantBadge", { defaultValue: "Instant answer" });

  const buildMessage = useCallback(
    (role: AssistantMessage["role"], content: string, extras?: Partial<AssistantMessage>): AssistantMessage => ({
      id: generateMessageId(),
      role,
      content,
      timestamp: new Date(),
      ...extras,
    }),
    [],
  );

  const handleSend = async (text?: string) => {
    const messageText = text ?? input.trim();
    if (!messageText) return;

    if (disabled) {
      setErrorMessage(disabledCopy);
      return;
    }

    const userMessage = buildMessage("user", messageText);

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);
    let instantAnswerId: string | null = null;
    const instantAnswer = resolveInstantAnswer?.(messageText, conversationId);
    if (instantAnswer) {
      const assistantMessage = buildMessage("assistant", instantAnswer.reply, {
        badgeLabel: instantAnswer.badgeLabel ?? assistantInstantBadge,
        source: "instant",
      });
      instantAnswerId = assistantMessage.id;
      setMessages((prev) => [...prev, assistantMessage]);
    }

    try {
      const result = await onSendMessage(messageText, conversationId, {
        intentId: instantAnswer?.intentId ?? null,
      });
      const normalizedReply = typeof result.reply === "string" ? result.reply.trim() : "";

      if (!disabled) {
        if (result.quotaStatus && result.quotaStatus !== "ok" && result.quotaStatus !== "unlimited") {
          const quotaMessage =
            typeof result.quotaMessage === "string" && result.quotaMessage.trim().length > 0
              ? result.quotaMessage.trim()
              : t("aiAssistant.quotaWarning", {
                  defaultValue:
                    "We are close to the monthly assistant limit, so responses will be shorter.",
                });
          setStatusOverride(quotaMessage);
        } else {
          setStatusOverride(null);
        }

        if (result.resolutionTier) {
          const tier = result.resolutionTier;
          let label: string | null = null;
          if (tier === "local_guard" || tier === "cache_exact" || tier === "cache_semantic") {
            label = t("assistant.instantBadge", { defaultValue: "Instant answer" });
          } else if (tier === "fast_model") {
            label = t("aiAssistant.resolutionFast", {
              defaultValue: "AI answer (fast model)",
            });
          } else if (tier === "premium_model") {
            label = t("aiAssistant.resolutionPremium", {
              defaultValue: "AI answer (premium model)",
            });
          }
          setLastResolutionLabel(label);
        }
      }

      if (instantAnswer && instantAnswerId) {
        if (normalizedReply.length > 0) {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === instantAnswerId
                ? {
                    ...message,
                    content: result.reply,
                    source: "assistant",
                  }
                : message,
            ),
          );
        } else {
          // Keep the vetted instant answer content; just mark it as coming from the assistant.
          setMessages((prev) =>
            prev.map((message) =>
              message.id === instantAnswerId
                ? {
                    ...message,
                    source: "assistant",
                  }
                : message,
            ),
          );
        }
      } else {
        const assistantMessage = buildMessage(
          "assistant",
          normalizedReply.length > 0 ? result.reply : fallbackMessageCopy,
        );
        setMessages((prev) => [...prev, assistantMessage]);
      }
      setConversationId(result.conversationId ?? conversationId);
      setErrorMessage(normalizedReply.length > 0 ? null : fallbackMessageCopy);
    } catch (error) {
      const fallbackMessage =
        error instanceof Error ? error.message : "Something went wrong. Please try again.";
      if (!disabled && error instanceof ApiResponseError) {
        const payload = error.body as { code?: unknown } | null | undefined;
        const code = typeof payload?.code === "string" ? payload.code : null;
        if (code === "AI_COOLDOWN") {
          setStatusOverride(
            t("aiAssistant.rateLimited", {
              defaultValue:
                "You're at the assistant limit for this minute. Give it a moment and try again.",
            }),
          );
        } else if (code === "AI_QUOTA_EXCEEDED") {
          setStatusOverride(
            t("aiAssistant.quotaWarning", {
              defaultValue:
                "We are close to the monthly assistant limit, so responses will be shorter.",
            }),
          );
        }
      }
      if (!instantAnswer || !instantAnswerId) {
        const assistantMessage = buildMessage("assistant", fallbackMessage);
        setMessages((prev) => [...prev, assistantMessage]);
      }
      setErrorMessage(fallbackMessage);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.96 }}
        transition={{ type: "spring", stiffness: 260, damping: 18 }}
        onClick={() => setIsOpen(true)}
        aria-label={heroCopy.title}
        className="group fixed bottom-6 right-4 z-50 flex items-center gap-3 rounded-full bg-gradient-to-br from-emerald-400 via-emerald-600 to-emerald-700 px-5 py-3 text-white shadow-[0_25px_60px_-30px_rgba(16,185,129,1)]"
      >
        <motion.span
          aria-hidden="true"
          animate={{ opacity: [0.2, 0.6, 0.2], scale: [1, 1.25, 1] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -inset-3 rounded-full bg-emerald-400/40 blur-3xl"
        />
        <motion.span
          aria-hidden="true"
          animate={{ scale: [1, 1.12, 1], opacity: [0.4, 0.85, 0.4] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 rounded-full border border-emerald-50/40"
        />
        <div className="relative flex items-center gap-2">
          <AssistantGlyph />
          <div className="flex flex-col leading-tight text-left">
            <span className="text-[10px] uppercase tracking-[0.45em] text-emerald-50/80">Ask</span>
            <span className="text-base font-semibold">Braisey</span>
          </div>
        </div>
      </motion.button>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side="bottom"
          data-language={language}
          className="flex h-[90vh] flex-col overflow-hidden border-t border-emerald-100 bg-gradient-to-b from-white via-emerald-50/30 to-white p-0"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Ask Braisey assistant</SheetTitle>
            <SheetDescription>Chat with the restaurant AI assistant for menu help.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 space-y-4 overflow-hidden px-4 pb-6 pt-5 md:px-8 md:pt-8">
            <div className="grid h-full gap-4 md:grid-cols-[1.05fr_0.95fr]">
              <motion.section
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="flex h-full flex-col overflow-hidden rounded-3xl border border-emerald-100/80 bg-white/90 shadow-2xl shadow-emerald-900/5 backdrop-blur"
              >
                <div className="border-b border-emerald-100/80 bg-gradient-to-br from-white to-emerald-50/40 px-5 py-5">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-start gap-3">
                      <motion.div
                        animate={{ rotate: [0, 6, -6, 0] }}
                        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                        className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700"
                      >
                        <BrainCircuit className="h-5 w-5" />
                      </motion.div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-emerald-500">{heroCopy.badge}</p>
                        <p className="text-lg font-semibold text-zinc-900">{heroCopy.title}</p>
                        <p className="text-sm text-zinc-600">{heroCopy.subtitle}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-start gap-2 text-xs text-zinc-500 md:items-end">
                      <div className="flex items-center gap-2 rounded-full border border-emerald-100/70 bg-white/80 px-3 py-1 text-emerald-600">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                        <span className="font-semibold uppercase tracking-wide">{statusLabel}</span>
                        <span className="text-emerald-700">
                          {statusValue}
                          {lastResolutionLabel ? ` · ${lastResolutionLabel}` : ""}
                        </span>
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50/80 px-3 py-1 text-emerald-700">
                        <Clock className="h-3.5 w-3.5" />
                        {heroCopy.uptime}
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-full bg-zinc-900 px-3 py-1 text-white">
                        <Sparkles className="h-3 w-3 text-emerald-200" />
                        {heroCopy.metricLabel}
                        <span className="font-semibold text-emerald-100">{heroCopy.metricValue}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-1 flex-col overflow-hidden">
                  <div className="border-b border-emerald-50 px-5 py-4">
                    <p className="text-xs uppercase tracking-[0.35em] text-emerald-500">{heroCopy.suggestionIntro}</p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-3">
                      {assistantSuggestions.map((suggestion, index) => (
                        <motion.button
                          key={`${suggestion.title}-${index}`}
                          whileHover={{ y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleSend(suggestion.prompt)}
                          disabled={disabled}
                          className={cn(
                            "group rounded-2xl border border-emerald-100/70 bg-white/80 p-3 text-left text-sm shadow-sm transition-colors hover:border-emerald-200 hover:bg-emerald-50/60",
                            disabled && "cursor-not-allowed opacity-50",
                          )}
                        >
                          <p className="font-medium text-zinc-900">{suggestion.title}</p>
                          <p className="mt-1 text-xs text-zinc-500">{suggestion.description}</p>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
                    {messages.length === 0 && (
                      <motion.div
                        initial={{ opacity: 0.6 }}
                        animate={{ opacity: 1 }}
                        className="rounded-3xl border border-dashed border-emerald-200 bg-emerald-50/40 p-5 text-center text-sm text-zinc-600"
                      >
                        {emptyState}
                      </motion.div>
                    )}
                    <AnimatePresence initial={false}>
                      {messages.map((message, index) => {
                        const isAssistant = message.role === "assistant";
                        return (
                          <motion.div
                            key={message.id ?? `${message.timestamp.toISOString()}-${index}`}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 12 }}
                            className={cn("flex", isAssistant ? "justify-start" : "justify-end")}
                          >
                            <div
                              className={cn(
                                "max-w-[85%] rounded-3xl border px-4 py-3 text-sm shadow-sm",
                                isAssistant
                                  ? "border-emerald-100 bg-emerald-50/70 text-zinc-900"
                                  : "border-zinc-900 bg-zinc-900 text-white",
                              )}
                            >
                              <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide">
                                <span>{isAssistant ? assistantLabel : guestLabel}</span>
                                {message.badgeLabel && (
                                  <span className="rounded-full bg-white/40 px-2 py-0.5 text-[10px] text-emerald-700">
                                    {message.badgeLabel}
                                  </span>
                                )}
                              </div>
                              <p className="leading-relaxed">{message.content}</p>
                              <p
                                className={cn(
                                  "mt-2 text-[10px]",
                                  isAssistant ? "text-emerald-800/70" : "text-white/70",
                                )}
                              >
                                {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </p>
                            </div>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                    {isTyping && (
                      <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex justify-start"
                      >
                        <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 px-4 py-3 text-sm text-emerald-800 shadow-sm">
                          <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide">
                            <span>{assistantLabel}</span>
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                          </div>
                          <div className="flex gap-1.5">
                            {[0, 0.15, 0.3].map((delay) => (
                              <motion.span
                                key={delay}
                                animate={{ y: [0, -6, 0] }}
                                transition={{ duration: 0.7, repeat: Infinity, ease: "easeInOut", delay }}
                                className="h-2 w-2 rounded-full bg-emerald-500/70"
                              />
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  <div className="border-t border-emerald-50 bg-white/80 px-5 py-4 backdrop-blur">
                    <div className="space-y-2">
                      <div className="flex items-end gap-3">
                        <div className="relative flex-1">
                          <Textarea
                            value={input}
                            onChange={(event) => setInput(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" && !event.shiftKey) {
                                event.preventDefault();
                                handleSend();
                              }
                            }}
                            placeholder={placeholder}
                            disabled={disabled}
                            className="min-h-[64px] rounded-2xl border-emerald-100/70 bg-white/90 pr-12 text-sm shadow-inner shadow-emerald-900/5 focus-visible:ring-emerald-200"
                          />
                          <motion.div
                            animate={{ scale: input ? [1, 1.15, 1] : 1 }}
                            transition={{ duration: 1.2, repeat: input ? Infinity : 0, ease: "easeInOut" }}
                            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-emerald-500"
                          >
                            <Sparkles className="h-4 w-4" />
                          </motion.div>
                        </div>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button
                            onClick={() => handleSend()}
                            size="icon"
                            aria-label={sendAria}
                            disabled={disabled}
                            className="h-14 w-14 rounded-2xl bg-zinc-900 text-white shadow-lg shadow-emerald-600/30 hover:bg-zinc-800 disabled:opacity-50"
                          >
                            <Send className="h-5 w-5" />
                            <span className="sr-only">{sendButtonLabel}</span>
                          </Button>
                        </motion.div>
                      </div>
                      {errorMessage && <p className="text-xs text-red-500">{errorMessage}</p>}
                    </div>
                  </div>
                </div>
              </motion.section>
              <MarketingPanel
                className="hidden md:flex"
                heroSubtitle={heroCopy.subtitle}
                insightsTitle={marketingInsightsTitle}
                insights={marketingInsights}
                story={marketingStory}
                promise={marketingPromise}
              />
            </div>
            <MarketingPanel
              className="md:hidden"
              heroSubtitle={heroCopy.subtitle}
              insightsTitle={marketingInsightsTitle}
              insights={marketingInsights}
              story={marketingStory}
              promise={marketingPromise}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

interface PhoneVerificationPageProps {
  onVerified: (payload: { phone: string; guestName: string }) => void;
  language: "en" | "es";
  initialPhone?: string;
  initialName?: string;
  venueName?: string;
}

function PhoneVerificationPage({
  onVerified,
  language,
  initialPhone = "",
  initialName = "",
  venueName = DEFAULT_VENUE_INFO.name,
}: PhoneVerificationPageProps) {
  const [phone, setPhone] = useState(() => normalizePhoneInputValue(initialPhone));
  const [guestNameValue, setGuestNameValue] = useState(() => initialName.trim());
  const [error, setError] = useState("");
  useEffect(() => {
    const normalized = normalizePhoneInputValue(initialPhone);
    if (normalized.length === 0) return;
    setPhone((current) => (current.trim().length > 0 ? current : normalized));
  }, [initialPhone]);
  useEffect(() => {
    const normalized = initialName.trim();
    if (normalized.length === 0) return;
    setGuestNameValue((current) => (current.trim().length > 0 ? current : normalized));
  }, [initialName]);

  const content = {
    en: {
      title: `Welcome to ${venueName}`,
      subtitle: "Enter your phone to continue",
      phoneLabel: "Phone number",
      benefits: ["Quick orders", "Save favorites", "Track status"],
      privacy: "Secure & private",
      nameLabel: "Your name",
      namePlaceholder: "What should we call you?",
      nameError: "Please share your name so the kitchen can personalize your order.",
      placeholder: "+1 (555) 000-0000",
      button: "Continue",
      errorInvalid: "Please enter a valid phone number",
    },
    es: {
      title: `Bienvenido a ${venueName}`,
      subtitle: "Ingrese su teléfono para continuar",
      phoneLabel: "Número de teléfono",
      benefits: ["Pedidos rápidos", "Guardar favoritos", "Seguir estado"],
      privacy: "Seguro y privado",
      nameLabel: "Tu nombre",
      namePlaceholder: "¿Cómo te llamas?",
      nameError: "Comparte tu nombre para personalizar tu pedido.",
      placeholder: "+1 (555) 000-0000",
      button: "Continuar",
      errorInvalid: "Por favor ingrese un número de teléfono válido",
    },
  };

  const t = content[language];

  const handleSubmit = () => {
    const cleaned = extractPhoneDigits(phone);
    if (guestNameValue.trim().length === 0) {
      setError(t.nameError);
      return;
    }
    if (cleaned.length !== TEN_DIGIT_PHONE_LENGTH) {
      setError(t.errorInvalid);
      return;
    }
    setError("");
    onVerified({ phone: `+${cleaned}`, guestName: guestNameValue.trim() });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-white to-zinc-100 p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-2 text-center">
          <div className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-zinc-900">
            <Phone className="h-7 w-7 text-white" />
          </div>
          <h1>{t.title}</h1>
          <p className="text-sm text-zinc-600">{t.subtitle}</p>
        </div>

        <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-3 gap-3 text-center">
            {t.benefits.map((benefit) => (
              <div key={benefit} className="space-y-1.5">
                <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100">
                  <Check className="h-4 w-4 text-zinc-900" />
                </div>
                <span className="block text-xs leading-tight text-zinc-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="guest-name" className="text-sm font-medium text-zinc-700">
              {t.nameLabel}
            </Label>
            <Input
              id="guest-name"
              type="text"
              autoComplete="name"
              placeholder={t.namePlaceholder}
              value={guestNameValue}
              onChange={(event) => {
                setGuestNameValue(event.target.value);
                setError("");
              }}
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="guest-phone" className="text-sm font-medium text-zinc-700">
              {t.phoneLabel}
            </Label>
            <Input
              id="guest-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder={t.placeholder}
              value={phone}
              onChange={(event) => {
                setPhone(normalizePhoneInputValue(event.target.value));
                setError("");
              }}
              className="h-12 text-center"
            />
          </div>
          {error && <p className="text-center text-sm text-red-600">{error}</p>}
          <Button onClick={handleSubmit} className="h-12 w-full bg-zinc-900 hover:bg-zinc-800">
            {t.button}
          </Button>
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-zinc-500">
          <Shield className="h-3.5 w-3.5" />
          <span>{t.privacy}</span>
        </div>
      </div>
    </div>
  );
}

interface LinkIssueCopy {
  title: string;
  description: string;
  retry: string;
  support: string;
  issues: Record<TableOrderLinkIssue, string>;
}

interface LinkIssuePageProps {
  issues: TableOrderLinkIssue[];
  copy: LinkIssueCopy;
}

function LinkIssuePage({ issues, copy }: LinkIssuePageProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-white to-zinc-100 p-4">
      <div className="w-full max-w-md space-y-5 rounded-2xl border border-zinc-200 bg-white p-6 text-center shadow-xl">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <div className="space-y-2">
          <h1>{copy.title}</h1>
          <p className="text-sm text-zinc-600">{copy.description}</p>
        </div>
        <div className="space-y-2 text-left">
          {issues.map((issue) => (
            <div
              key={issue}
              className="rounded-xl border border-amber-200 bg-amber-50/80 p-3 text-amber-900"
              data-testid={`link-issue-${issue}`}
            >
              <p className="text-sm font-medium">{copy.issues[issue]}</p>
              <p className="text-xs font-mono uppercase tracking-wide text-amber-600">{LINK_ISSUE_CODES[issue]}</p>
            </div>
          ))}
        </div>
        <Button onClick={() => window.location.reload()} className="h-11 w-full bg-zinc-900 hover:bg-zinc-800">
          {copy.retry}
        </Button>
        <p className="text-xs text-zinc-500">{copy.support}</p>
      </div>
    </div>
  );
}

export default function TableOrderPage(): JSX.Element {
  const { subdomain, qrSlug } = useParams<{ subdomain?: string; qrSlug?: string }>();
  const [searchParams] = useSearchParams();
  const { t: translate, i18n } = useTranslation(["common", "public"]);
  const { toast } = useToast();

  const tokenParam = (searchParams.get("token") ?? "").trim();
  const modeParam = (searchParams.get("mode") ?? "").trim().toLowerCase();
  const isDemoMode = modeParam === "demo";
  const tokenPresent = tokenParam.length > 0;

  const tenantSlug = (subdomain ?? "").trim();
  const tableSlug = (qrSlug ?? "").trim();
  const tenantKey = tenantSlug || ANON_TENANT;
  const tableKey = tableSlug || DEFAULT_TABLE;
  const linkIssues = useMemo<TableOrderLinkIssue[]>(() => {
    const issues: TableOrderLinkIssue[] = [];
    if (!tenantSlug) {
      issues.push("tenant");
    }
    if (!tableSlug) {
      issues.push("table");
    }
    if (!isDemoMode && !tokenPresent) {
      issues.push("token");
    }
    return issues;
  }, [isDemoMode, tableSlug, tenantSlug, tokenPresent]);
  const linkIssueCopy = useMemo<LinkIssueCopy>(
    () => ({
      title: translate("public:linkIssues.title", {
        defaultValue: "We can’t load this menu yet",
      }),
      description: translate("public:linkIssues.description", {
        defaultValue: "This QR link is missing required info. Please scan a fresh code or ask the team to regenerate it.",
      }),
      retry: translate("public:linkIssues.retry", { defaultValue: "Try again" }),
      support: translate("public:linkIssues.support", {
        defaultValue: "Share this message with the Braise team so we can refresh the QR code.",
      }),
      issues: {
        tenant: translate("public:linkIssues.missingTenant", {
          defaultValue: "The restaurant identifier was missing from this URL.",
        }),
        table: translate("public:linkIssues.missingTable", {
          defaultValue: "The table slug was missing or invalid.",
        }),
        token: translate("public:linkIssues.missingToken", {
          defaultValue: "The QR token was missing, so we could not verify this link.",
        }),
      },
    }),
    [translate],
  );

  const languageStorageKey = buildStorageKey(tenantKey, tableKey, "language");
  const favoritesStorageKey = buildStorageKey(tenantKey, tableKey, "favorites");
  const phoneStorageKey = buildStorageKey(tenantKey, tableKey, "phone");
  const guestNameStorageKey = buildStorageKey(tenantKey, tableKey, "guest_name");
  const assistantStorageKey = buildStorageKey(tenantKey, tableKey, "assistant");
  const guestTokenStorageKey = buildTenantStorageKey(tenantKey, "guestToken");
  const anonymousGuestIdStorageKey = buildTenantStorageKey(tenantKey, "guestSessionId");

  const [language, setLanguageState] = useState<"en" | "es">(() => {
    if (typeof window === "undefined") return "en";
    const saved = localStorage.getItem(languageStorageKey);
    return saved === "es" ? "es" : "en";
  });

  const [phoneNumber, setPhoneNumber] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(phoneStorageKey) ?? "";
  });
  const [phoneVerified, setPhoneVerified] = useState(() => phoneNumber.trim().length > 0);
  const [guestNameInput, setGuestNameInput] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(guestNameStorageKey) ?? "";
  });

  const [guestToken, setGuestTokenState] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(guestTokenStorageKey);
  });

  const [anonymousGuestId, setAnonymousGuestId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const existing = localStorage.getItem(anonymousGuestIdStorageKey);
    if (existing && existing.trim().length > 0) {
      return existing;
    }
    const generated = `g_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(anonymousGuestIdStorageKey, generated);
    return generated;
  });

  const persistGuestToken = useCallback(
    (value: string | null) => {
      setGuestTokenState(value);
      if (typeof window === "undefined") {
        return;
      }
      if (value) {
        localStorage.setItem(guestTokenStorageKey, value);
      } else {
        localStorage.removeItem(guestTokenStorageKey);
      }
    },
    [guestTokenStorageKey],
  );
  const diagnosticsSnapshot = useMemo<TableOrderDiagnosticsSnapshot>(
    () => ({
      tenantSlug,
      tableSlug,
      tokenPresent,
      tokenPreview: maskTokenPreview(tokenParam),
      phoneVerified,
      phonePreview: maskPhonePreview(phoneNumber),
      guestTokenPresent: Boolean(guestToken),
      isDemoMode,
      storageKeys: {
        phone: phoneStorageKey,
        language: languageStorageKey,
        favorites: favoritesStorageKey,
        assistant: assistantStorageKey,
        guestToken: guestTokenStorageKey,
        guestSessionId: anonymousGuestIdStorageKey,
      },
      timestamp: new Date().toISOString(),
    }),
    [
      assistantStorageKey,
      favoritesStorageKey,
      guestToken,
      guestTokenStorageKey,
      isDemoMode,
      languageStorageKey,
      phoneNumber,
      phoneStorageKey,
      phoneVerified,
      tableSlug,
      tenantSlug,
      tokenParam,
      tokenPresent,
    ],
  );
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.__BRAISE_TABLE_ORDER_DIAGNOSTICS__ = diagnosticsSnapshot;
    const logger = diagnosticsSnapshot.tokenPresent ? console.info : console.warn;
    logger.call(console, "[table-order] gating diagnostics", diagnosticsSnapshot);
    return () => {
      if (window.__BRAISE_TABLE_ORDER_DIAGNOSTICS__ === diagnosticsSnapshot) {
        delete window.__BRAISE_TABLE_ORDER_DIAGNOSTICS__;
      }
    };
  }, [diagnosticsSnapshot]);

  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => {
    if (typeof window === "undefined") return new Set();
    const saved = localStorage.getItem(favoritesStorageKey);
    if (!saved) return new Set();
    try {
      return new Set(JSON.parse(saved) as string[]);
    } catch {
      return new Set();
    }
  });

  const [assistantState, setAssistantState] = useState<{ messages: AssistantMessage[]; conversationId: string | null }>(() => {
    if (typeof window === "undefined") {
      return { messages: [], conversationId: null };
    }
    const saved = localStorage.getItem(assistantStorageKey);
    if (!saved) {
      return { messages: [], conversationId: null };
    }
    try {
      const parsed = JSON.parse(saved) as {
        messages?: {
          id?: string;
          role: AssistantMessage["role"];
          content: string;
          badgeLabel?: string | null;
          timestamp: string;
        }[];
        conversationId?: string | null;
      };
      return {
        messages:
          parsed.messages?.map((message) => ({
            id: message.id ?? generateMessageId(),
            role: message.role,
            content: message.content,
            badgeLabel: message.badgeLabel ?? null,
            timestamp: new Date(message.timestamp),
          })) ?? [],
        conversationId: parsed.conversationId ?? null,
      };
    } catch {
      return { messages: [], conversationId: null };
    }
  });

  const [assistantMessages, setAssistantMessages] = useState<AssistantMessage[]>(assistantState.messages);
  const [assistantConversationId, setAssistantConversationId] = useState<string | null>(assistantState.conversationId);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [orderNote, setOrderNote] = useState("");

  const mainCartItemIds = useMemo(() => {
    const ids = new Set<string>();
    for (const line of cart) {
      if (line.quantity > 0) {
        ids.add(line.item.id);
      }
    }
    return Array.from(ids);
  }, [cart]);

  const mainCartFingerprint = useMemo(() => {
    if (mainCartItemIds.length === 0) {
      return "empty";
    }
    return [...mainCartItemIds].sort().join(",");
  }, [mainCartItemIds]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(languageStorageKey, language);
    const changeLanguage = i18n?.changeLanguage;
    if (typeof changeLanguage === "function") {
      Promise.resolve(changeLanguage.call(i18n, language)).catch(() => {});
    }
  }, [language, languageStorageKey, i18n]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(assistantStorageKey, JSON.stringify({
      conversationId: assistantConversationId,
      messages: assistantMessages.map((message) => ({
        id: message.id,
        role: message.role,
        content: message.content,
        badgeLabel: message.badgeLabel ?? null,
        timestamp: message.timestamp.toISOString(),
      })),
    }));
  }, [assistantConversationId, assistantMessages, assistantStorageKey]);

  const canResolveTable = isDemoMode || (tenantSlug.length > 0 && tableSlug.length > 0 && tokenParam.length > 0);
  const menuQuery = useQuery<PublicTableResponse>({
    enabled: canResolveTable,
    queryKey: ["public-table", tenantKey, tableKey, tokenParam, guestToken ?? "", language],
    queryFn: async ({ signal }) => {
      if (isDemoMode) {
        return buildDemoTableResponse({ tenantSlug: tenantKey, tableSlug: tableKey });
      }
      if (!tenantSlug || !tableSlug || !tokenParam) {
        throw new Error("Missing table information.");
      }
      return getPublicTable(tenantSlug, tableSlug, tokenParam, {
        guestToken: guestToken ?? undefined,
        signal,
      });
    },
    staleTime: 60_000,
  });

  const assistantConfigQuery = useQuery<PublicAssistantFeatureConfig>({
    enabled: !isDemoMode && canResolveTable && phoneVerified,
    queryKey: ["assistant-config", tenantKey, tokenParam],
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        throw new Error("Missing tenant information.");
      }
      return fetchAssistantFeatureConfig({ tenantSlug, token: tokenParam, signal });
    },
  });
  const assistantFeatures = assistantConfigQuery.data ?? null;

  const resolveAssistantInstantAnswer = useCallback(
    (message: string, _conversationId: string | null) => {
      if (
        !assistantFeatures ||
        !assistantFeatures.instantGuard.enabled ||
        assistantFeatures.instantGuard.shadowMode
      ) {
        return null;
      }
      const locale = language === "es" ? "es" : "en";
      const library = assistantFeatures.guardLibrary;
      const match = matchGuestIntent(message, locale, { library });
      if (!match) {
        return null;
      }
      const badgeLabel =
        match.badgeLabel ??
        translate("public:assistant.instantBadge", {
          defaultValue: locale === "es" ? "Respuesta inmediata" : "Instant answer",
        });
      return {
        reply: match.response,
        badgeLabel,
        intentId: match.id,
      };
    },
    [assistantFeatures, language, translate],
  );

  const tableData = menuQuery.data ?? null;
  const tenantDisplayName = tableData?.tenant?.name?.trim() ?? "";
  const venueDisplayName = useMemo(() => {
    if (tenantDisplayName.length > 0) {
      return tenantDisplayName;
    }
    if (tenantSlug.length > 0) {
      return humanizeTenantSlug(tenantSlug);
    }
    return DEFAULT_VENUE_INFO.name;
  }, [tenantDisplayName, tenantSlug]);
  const guestProfilePayload = tableData?.guest_profile ?? null;
  const remoteGuestName = guestProfilePayload?.name?.trim() ?? "";
  const guestName = guestNameInput.trim().length > 0 ? guestNameInput.trim() : remoteGuestName;
  useEffect(() => {
    if (remoteGuestName.length === 0) {
      return;
    }
    setGuestNameInput((current) => {
      if (current.trim().length > 0) {
        return current;
      }
      return remoteGuestName;
    });
  }, [remoteGuestName]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const normalized = guestNameInput.trim();
    if (normalized.length === 0) {
      localStorage.removeItem(guestNameStorageKey);
      return;
    }
    localStorage.setItem(guestNameStorageKey, normalized);
  }, [guestNameInput, guestNameStorageKey]);
  const locationId = tableData?.location?.id ?? null;

  const recommendationSignalsQuery = useQuery<PublicRecommendationSignal[]>({
    enabled: Boolean(!isDemoMode && tenantSlug && tableData && phoneVerified),
    queryKey: ["public", "recommendations", "signals", tenantSlug, locationId],
    staleTime: 120_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve([]);
      }
      return getRecommendationSignals({
        tenantSlug,
        locationId: locationId ?? undefined,
        signal,
      });
    },
  });

  const engagementSignals = useMemo(() => {
    const signals = recommendationSignalsQuery.data ?? [];
    if (signals.length === 0) {
      return null;
    }
    return new Map<string, number>(signals.map((entry) => [entry.menuItemId, entry.score]));
  }, [recommendationSignalsQuery.data]);
  const personalizationAllowed = useMemo(
    () => resolvePersonalizationConsent(guestProfilePayload?.consent),
    [guestProfilePayload?.consent],
  );
  const rawGuestTaste = useMemo(
    () => buildGuestTasteSnapshot(guestProfilePayload),
    [guestProfilePayload],
  );
  const guestProfile = useMemo<GuestProfile>(
    () => ({
      favorites: Array.from(favoriteIds),
      name: guestProfilePayload?.name ?? undefined,
      phone: guestProfilePayload?.external_id ?? undefined,
      externalId: guestProfilePayload?.external_id ?? null,
      tasteTags: rawGuestTaste?.tags ?? [],
      tasteCuisines: rawGuestTaste?.cuisines ?? [],
      tasteHeat: rawGuestTaste?.heat ?? null,
      consentPersonalization: personalizationAllowed,
    }),
    [
      favoriteIds,
      guestProfilePayload?.external_id,
      guestProfilePayload?.name,
      personalizationAllowed,
      rawGuestTaste,
    ],
  );
  const guestTasteSnapshot = personalizationAllowed ? rawGuestTaste : null;
  const combinedFavorites = useMemo(() => {
    return new Set(favoriteIds);
  }, [favoriteIds]);
  const recentItemIds = useMemo(() => {
    return new Set(extractSnapshotItemIds(guestProfilePayload?.last_order_snapshot ?? null));
  }, [guestProfilePayload?.last_order_snapshot]);

  const syncGuestProfilePreferences = useCallback(
    (
      favorites: string[],
      options?: {
        consentPersonalization?: boolean;
        guestPhoneOverride?: string | null;
        guestNameOverride?: string | null;
      },
    ) => {
      if (isDemoMode || !tenantSlug) return;
      const activeToken = guestProfilePayload?.external_id ?? guestToken;
      const overridePhone =
        typeof options?.guestPhoneOverride === "string" && options.guestPhoneOverride.trim().length > 0
          ? options.guestPhoneOverride.trim()
          : null;
      const phoneValue =
        overridePhone ?? (phoneVerified && phoneNumber.trim().length > 0 ? phoneNumber.trim() : null);
      if (!activeToken && !phoneValue) {
        return;
      }
      const normalizedFavorites = Array.from(
        new Set(favorites.map((id) => id.trim()).filter((id) => id.length > 0)),
      ).slice(0, 50);

      const normalizedGuestName =
        typeof options?.guestNameOverride === "string" && options.guestNameOverride.trim().length > 0
          ? options.guestNameOverride.trim()
          : guestName.trim().length > 0
            ? guestName.trim()
            : undefined;

      syncGuestProfile({
        tenantSlug,
        guestToken: activeToken ?? undefined,
        guestPhone: phoneValue ?? undefined,
        guestName: normalizedGuestName,
        favorites: normalizedFavorites,
        consentPersonalization: options?.consentPersonalization,
      })
        .then((response) => {
          if (response.guestToken && response.guestToken !== activeToken) {
            persistGuestToken(response.guestToken);
          }
          if (response.favorites) {
            setFavoriteIds(new Set(response.favorites));
            if (typeof window !== "undefined") {
              localStorage.setItem(favoritesStorageKey, JSON.stringify(response.favorites));
            }
          }
        })
        .catch((error) => {
          console.error("[public] failed to sync guest profile", error);
        });
    },
    [
      favoritesStorageKey,
      guestName,
      guestProfilePayload?.external_id,
      guestToken,
      isDemoMode,
      persistGuestToken,
      phoneNumber,
      phoneVerified,
      tenantSlug,
    ],
  );

  useEffect(() => {
    if (!tableData) return;
    const externalId = guestProfilePayload?.external_id ?? null;
    if (externalId && externalId !== guestToken) {
      persistGuestToken(externalId);
    }
  }, [guestProfilePayload, tableData, guestToken, persistGuestToken]);

  const categoryNameLookup = useMemo(() => {
    if (!tableData) return new Map<string, string>();
    const map = new Map<string, string>();
    tableData.categories.forEach((category) => {
      const translated =
        category.translations?.[language]?.name ?? category.name ?? (language === "es" ? "Sin categoría" : "Uncategorized");
      map.set(category.id, translated);
    });
    return map;
  }, [tableData, language]);

  const fallbackCategoryName = language === "es" ? "Sin categoría" : "Uncategorized";

  const menuItems = useMemo<MenuItem[]>(() => {
    if (!tableData) return [];
    return tableData.items
      .filter((item) => item.is_available)
      .map((item) => {
        const tasteProfile = (item.taste_profile ?? {}) as Record<string, unknown>;
        const metadataTags = Array.isArray(item.metadata?.tags) ? item.metadata?.tags : [];
        const tagScoresKeys = Object.keys(
          mergeNumberRecords(
            coerceNumberRecord((tasteProfile as { tagScores?: unknown }).tagScores),
            coerceNumberRecord((tasteProfile as { tag_scores?: unknown }).tag_scores),
          ),
        );
        const tagCandidates = [
          ...(Array.isArray(item.tags) ? item.tags : []),
          ...metadataTags,
          ...((item.metadata?.dietaries as string[] | undefined) ?? []),
          ...toUniqueStringList((tasteProfile as { tags?: unknown }).tags),
          ...tagScoresKeys,
        ];
        const itemTagsSet = new Set<string>();
        for (const candidate of tagCandidates) {
          const normalized = normaliseTasteString(candidate);
          if (normalized) {
            itemTagsSet.add(normalized);
          }
        }
        const lowerTags = new Set(itemTagsSet);
        if (item.metadata?.popular || item.metadata?.isPopular) {
          lowerTags.add("popular");
        }
        const cuisineAffinityKeys = Object.keys(
          mergeNumberRecords(
            coerceNumberRecord((tasteProfile as { cuisineAffinity?: unknown }).cuisineAffinity),
            coerceNumberRecord((tasteProfile as { cuisine_affinity?: unknown }).cuisine_affinity),
          ),
        );
        const cuisinesSet = new Set<string>();
        toUniqueStringList((tasteProfile as { cuisines?: unknown }).cuisines).forEach((cuisine) => cuisinesSet.add(cuisine));
        cuisineAffinityKeys.forEach((cuisine) => {
          const normalized = normaliseTasteString(cuisine);
          if (normalized) {
            cuisinesSet.add(normalized);
          }
        });
        const dietary: DietFilter[] = ["all"];
        if (lowerTags.has("vegetarian")) {
          dietary.push("vegetarian");
        }
        if (lowerTags.has("vegan")) {
          dietary.push("vegan");
        }
        const prepMeta =
          (item.metadata?.prepTime as string | undefined) ??
          (item.metadata?.prep_time as string | undefined) ??
          item.metadata?.prepTimeMinutes?.toString?.() ??
          item.metadata?.prep_time_minutes?.toString?.();
        const prepTimeCandidate =
          typeof item.prep_minutes === "number" && Number.isFinite(item.prep_minutes)
            ? item.prep_minutes
            : prepMeta
              ? Number.parseInt(prepMeta, 10)
              : null;
        const prepTime = typeof prepTimeCandidate === "number" && Number.isFinite(prepTimeCandidate) ? prepTimeCandidate : 10;
        const tasteHeat =
          normaliseTasteString((tasteProfile as { heat?: unknown }).heat) ??
          normaliseTasteString(item.image_spice_level) ??
          null;
        const modifierTranslationMap = new Map<string, string>();
        (item.modifiers ?? []).forEach((modifier) => {
          if (!modifier || typeof modifier.id !== "string") {
            return;
          }
          const localizedName = modifier.translations?.[language]?.name ?? modifier.name;
          if (typeof localizedName === "string" && localizedName.trim().length > 0) {
            modifierTranslationMap.set(modifier.id, localizedName.trim());
          }
        });
        const modifierGroups = normalizeModifierGroups(item).map((group) => ({
          ...group,
          modifiers: group.modifiers.map((modifier) => ({
            ...modifier,
            name: modifierTranslationMap.get(modifier.id) ?? modifier.name,
          })),
        }));
        const flattenedModifiers = modifierGroups.flatMap((group) =>
          group.modifiers.map((modifier) => ({
            ...modifier,
            groupId: modifier.groupId ?? group.id,
            groupName: modifier.groupName ?? group.name,
          })),
        );

        return {
          id: item.id,
          name: item.translations?.[language]?.name ?? item.name,
          description: item.translations?.[language]?.description ?? item.description ?? "",
          price: item.price_cents / 100,
          categoryId: item.category_id ?? "uncategorized",
          categoryName: categoryNameLookup.get(item.category_id ?? "") ?? fallbackCategoryName,
          dietary,
          image: item.image_url ?? "",
          popular: Boolean(
            item.metadata?.popular ??
            item.metadata?.isPopular ??
            (lowerTags.has("popular") || lowerTags.has("bestseller")),
          ),
          prepTime,
          marginCents: typeof item.margin_cents === "number" ? item.margin_cents : null,
          station: typeof item.station === "string" ? item.station : null,
          isNew: typeof item.is_new === "boolean" ? item.is_new : null,
          tags: Array.from(itemTagsSet),
          cuisines: Array.from(cuisinesSet),
          tasteHeat,
          modifiers: flattenedModifiers,
          modifierGroups,
        };
      });
  }, [categoryNameLookup, fallbackCategoryName, language, tableData]);
  const menuItemMap = useMemo(() => {
    const map = new Map<string, MenuItem>();
    for (const item of menuItems) {
      map.set(item.id, item);
    }
    return map;
  }, [menuItems]);
  const menuFingerprint = useMemo(() => menuItems.map((item) => item.id).join(","), [menuItems]);

  const serverRecommendationsQuery = useQuery({
    enabled: Boolean(!isDemoMode && tenantSlug && menuItems.length > 0 && phoneVerified),
    queryKey: [
      "public",
      "recommendations",
      "score",
      tenantSlug,
      guestToken ?? "",
      locationId ?? "unknown",
      menuFingerprint,
    ],
    staleTime: 60_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve(null);
      }
      return fetchRecommendationScores({
        tenantSlug,
        guestToken: guestToken ?? undefined,
        locationId: locationId ?? undefined,
        limit: 6,
        items: menuItems.map((item) => ({
          id: item.id,
          tags: item.tags,
          cuisines: item.cuisines,
          tasteHeat: item.tasteHeat,
          popular: item.popular,
          categoryId: item.categoryId,
          marginCents: item.marginCents ?? null,
          prepMinutes: item.prepTime ?? null,
          station: item.station ?? null,
          isNew: item.isNew ?? false,
        })),
        signal,
      }).then((result) => result?.recommendations ?? null);
    },
  });

  const attachRecommendationsQuery = useQuery({
    enabled: Boolean(!isDemoMode && tenantSlug && mainCartItemIds.length > 0 && phoneVerified),
    queryKey: [
      "public",
      "recommendations",
      "attach",
      tenantSlug,
      locationId ?? "global",
      mainCartFingerprint,
    ],
    staleTime: 30_000,
    queryFn: ({ signal }) => {
      if (!tenantSlug || mainCartItemIds.length === 0) {
        return Promise.resolve(null);
      }
      return fetchAttachRecommendations({
        tenantSlug,
        mainItemIds: mainCartItemIds,
        limit: 4,
        signal,
      });
    },
  });

  const remoteRecommendationState = useMemo<RecommendationState | null>(() => {
    const payload = serverRecommendationsQuery.data;
    if (!payload) {
      return null;
    }
    const orderedItems = payload.itemIds
      .map((id) => menuItemMap.get(id))
      .filter((item): item is MenuItem => Boolean(item));
    if (orderedItems.length === 0) {
      return null;
    }
    return {
      items: orderedItems,
      source: payload.source,
      breakdown: payload.breakdown,
      consentPersonalization: payload.consentPersonalization,
      tasteSnapshot: payload.tasteSnapshot,
      modelVersion: payload.modelVersion ?? undefined,
      guestProfileId: payload.guestProfileId ?? null,
      holdout: Boolean(payload.holdout),
    };
  }, [menuItemMap, serverRecommendationsQuery.data]);

  const localRecommendationState = useMemo<RecommendationState>(() => {
    if (menuItems.length === 0) {
      const fallback = computePopularFallback(menuItems);
      return {
        items: fallback.items,
        source: "popular",
        breakdown: fallback.breakdown,
        consentPersonalization: personalizationAllowed,
        tasteSnapshot: null,
        holdout: false,
      };
    }
    return buildRecommendationState({
      items: menuItems,
      favorites: combinedFavorites,
      recentItemIds,
      tasteSnapshot: guestTasteSnapshot,
      consentPersonalization: personalizationAllowed,
      engagementSignals,
      modelVersion: CLIENT_RECOMMENDATION_MODEL_VERSION,
    });
  }, [menuItems, combinedFavorites, recentItemIds, guestTasteSnapshot, personalizationAllowed, engagementSignals]);

  const recommendationState = remoteRecommendationState ?? localRecommendationState;

  const attachRecommendationState = useMemo(() => {
    const payload = attachRecommendationsQuery.data;
    if (!payload || !payload.recommendations || payload.recommendations.length === 0) {
      return null;
    }
    const hydrated: AttachRecommendationItem[] = [];
    const probabilities: Record<string, number> = {};
    for (const entry of payload.recommendations) {
      const item = menuItemMap.get(entry.itemId);
      if (!item) continue;
      hydrated.push({
        item,
        probability: entry.probability,
        matches: entry.matches,
      });
      probabilities[item.id] = entry.probability;
    }
    if (hydrated.length === 0) {
      return null;
    }
    return {
      items: hydrated,
      probabilities,
      modelVersion: payload.modelVersion ?? null,
      surface: payload.surface ?? ATTACH_SURFACE,
    };
  }, [attachRecommendationsQuery.data, menuItemMap]);

  const [recommendationImpression, setRecommendationImpression] = useState<{ key: string; id: string } | null>(null);
  const [attachRecommendationImpression, setAttachRecommendationImpression] = useState<{ key: string; id: string } | null>(null);

  useEffect(() => {
    if (isDemoMode) return;
    if (!recommendationState || recommendationState.items.length === 0) return;
    if (!tenantSlug || !tableSlug || tokenParam.length === 0 || !tableData) return;

    const recommendedItemIds = recommendationState.items.map((item) => item.id);
    if (recommendedItemIds.length === 0) return;

    const impressionKey = [tenantSlug, tableSlug, tokenParam, recommendedItemIds.join(",")].join("|");
    if (recommendationImpression?.key === impressionKey) return;

    const payload: RecommendationImpressionPayload = {
      tenantSlug,
      locationId: tableData.location?.id ?? null,
      tableId: tableData.table?.id ?? null,
      qrSlug: tableData.table?.qr_slug ?? tableSlug,
      profileSource: recommendationState.source,
      consentPersonalization: recommendationState.consentPersonalization,
      tags: recommendationState.tasteSnapshot?.tags,
      cuisines: recommendationState.tasteSnapshot?.cuisines,
      heat: recommendationState.tasteSnapshot?.heat ?? null,
      tagScores: recommendationState.tasteSnapshot?.tagScores,
      cuisineAffinity: recommendationState.tasteSnapshot?.cuisineAffinity,
      locale: language,
      recommendedItemIds,
      recommendedBundleIds: [],
      customerExternalId: guestProfilePayload?.external_id ?? null,
      guestProfileId: recommendationState.guestProfileId ?? null,
      sessionId: null,
      impressionKey,
      requestUrl: typeof window !== "undefined" ? window.location.href : null,
      modelVersion: recommendationState.modelVersion ?? CLIENT_RECOMMENDATION_MODEL_VERSION,
      surface: "table_order.hero",
      holdout: recommendationState.holdout ?? false,
    };

    recordRecommendationImpression(payload)
      .then((response) => {
        setRecommendationImpression({ key: impressionKey, id: response.id });
      })
      .catch((error) => {
        console.error("[public] failed to record recommendation impression", error);
      });
  }, [
    guestProfilePayload?.external_id,
    isDemoMode,
    language,
    recommendationImpression?.key,
    recommendationState,
    tableData,
    tableSlug,
    tenantSlug,
    tokenParam,
  ]);

  useEffect(() => {
    if (isDemoMode) return;
    if (!attachRecommendationState || attachRecommendationState.items.length === 0) {
      setAttachRecommendationImpression(null);
      return;
    }
    if (!tenantSlug || !tableSlug || tokenParam.length === 0 || !tableData) return;

    const recommendedItemIds = attachRecommendationState.items.map((entry) => entry.item.id);
    if (recommendedItemIds.length === 0) return;

    const impressionKey = [
      ATTACH_SURFACE,
      tenantSlug,
      tableSlug,
      tokenParam,
      mainCartFingerprint,
      recommendedItemIds.join(","),
    ].join("|");
    if (attachRecommendationImpression?.key === impressionKey) return;

    const payload: RecommendationImpressionPayload = {
      tenantSlug,
      locationId: tableData.location?.id ?? null,
      tableId: tableData.table?.id ?? null,
      qrSlug: tableData.table?.qr_slug ?? tableSlug,
      profileSource: "attach",
      consentPersonalization: false,
      recommendedItemIds,
      recommendedBundleIds: [],
      customerExternalId: guestProfilePayload?.external_id ?? null,
      guestProfileId: null,
      sessionId: null,
      impressionKey,
      requestUrl: typeof window !== "undefined" ? window.location.href : null,
      modelVersion: attachRecommendationState.modelVersion ?? ATTACH_MODEL_VERSION,
      surface: attachRecommendationState.surface ?? ATTACH_SURFACE,
      holdout: false,
    };

    recordRecommendationImpression(payload)
      .then((result) => {
        setAttachRecommendationImpression({ key: impressionKey, id: result.id });
      })
      .catch((error) => {
        console.error("[public] failed to record attach recommendation impression", error);
      });
  }, [
    attachRecommendationImpression?.key,
    attachRecommendationState,
    guestProfilePayload?.external_id,
    isDemoMode,
    mainCartFingerprint,
    tableData,
    tableSlug,
    tenantSlug,
    tokenParam,
  ]);

  const handleRecommendationAction = useCallback(
    (itemId: string, action: RecommendationActionType) => {
      if (isDemoMode) return;
      if (!recommendationImpression?.id || !tenantSlug) return;
      const slotIndex =
        recommendationState?.items.findIndex((entry) => entry.id === itemId) ?? -1;
      const breakdown = recommendationState?.breakdown[itemId];
      const metadata: Record<string, unknown> | undefined = breakdown
        ? {
            score: breakdown.score,
            reasons: breakdown.reasons,
            source: recommendationState.source,
            consentPersonalization: recommendationState.consentPersonalization,
          }
        : recommendationState
          ? {
              source: recommendationState.source,
              consentPersonalization: recommendationState.consentPersonalization,
            }
          : undefined;
      recordRecommendationAction({
        tenantSlug,
        impressionId: recommendationImpression.id,
        action,
        menuItemId: itemId,
        metadata,
        guestProfileId: recommendationState?.guestProfileId ?? null,
        slot: slotIndex >= 0 ? slotIndex : undefined,
        surface: "table_order.hero",
        modelVersion: recommendationState?.modelVersion ?? CLIENT_RECOMMENDATION_MODEL_VERSION,
        holdout: recommendationState?.holdout ?? false,
      }).catch((error) => {
        console.error("[public] failed to record recommendation action", error);
      });
    },
    [isDemoMode, recommendationImpression?.id, recommendationState, tenantSlug],
  );

  const handleAttachRecommendationAction = useCallback(
    (itemId: string, action: RecommendationActionType) => {
      if (isDemoMode) return;
      if (!attachRecommendationImpression?.id || !tenantSlug || !attachRecommendationState) return;
      const slotIndex =
        attachRecommendationState.items.findIndex((entry) => entry.item.id === itemId) ?? -1;
      const metadata: Record<string, unknown> = {
        source: "attach",
        probability: attachRecommendationState.probabilities[itemId],
      };
      recordRecommendationAction({
        tenantSlug,
        impressionId: attachRecommendationImpression.id,
        action,
        menuItemId: itemId,
        metadata,
        guestProfileId: null,
        slot: slotIndex >= 0 ? slotIndex : undefined,
        surface: ATTACH_SURFACE,
        modelVersion: attachRecommendationState.modelVersion ?? ATTACH_MODEL_VERSION,
        holdout: false,
      }).catch((error) => {
        console.error("[public] failed to record attach recommendation action", error);
      });
    },
    [attachRecommendationImpression?.id, attachRecommendationState, isDemoMode, tenantSlug],
  );

  const menuCategories = useMemo<CategoryOption[]>(() => {
    if (!tableData) return [];
    const categoriesWithItems = new Set(menuItems.map((item) => item.categoryId));
    const base = tableData.categories
      .filter((category) => categoriesWithItems.has(category.id))
      .map((category) => ({
        id: category.id,
        name: category.translations?.[language]?.name ?? category.name,
      }));
    if (categoriesWithItems.has("uncategorized")) {
      base.push({ id: "uncategorized", name: fallbackCategoryName });
    }
    return base;
  }, [fallbackCategoryName, language, menuItems, tableData]);

  const venueInfo = useMemo<VenueInfo>(() => {
    if (!tableData) return DEFAULT_VENUE_INFO;
    return {
      name: tableData.tenant?.name ?? DEFAULT_VENUE_INFO.name,
      rating: DEFAULT_VENUE_INFO.rating,
      reviewCount: DEFAULT_VENUE_INFO.reviewCount,
      prepTime: DEFAULT_VENUE_INFO.prepTime,
      location: tableData.location?.name ?? DEFAULT_VENUE_INFO.location,
      tableNumber: tableData.table?.table_number ? String(tableData.table.table_number) : DEFAULT_VENUE_INFO.tableNumber,
    };
  }, [tableData]);

  const calculateLineTotal = useCallback((line: CartItem) => {
    const modifiersTotal = line.modifiers.reduce((sum, modifier) => sum + modifier.priceDelta, 0);
    return (line.item.price + modifiersTotal) * line.quantity;
  }, []);

  const subtotal = useMemo(() => cart.reduce((sum, line) => sum + calculateLineTotal(line), 0), [cart, calculateLineTotal]);

  const handleAddToCart = useCallback(
    (item: MenuItem, quantity: number, modifierIds: string[], notes: string) => {
      const modifiers = item.modifiers?.filter((modifier) => modifierIds.includes(modifier.id)) ?? [];
      setCart((previous) => [...previous, { item, quantity, modifiers, notes }]);
      toast({ title: "Added to cart", description: item.name });
    },
    [toast],
  );

  const handleUpdateCartItem = useCallback((index: number, quantity: number) => {
    setCart((previous) => {
      const next = previous.map((line) => ({ ...line }));
      const target = next[index];
      if (!target) return previous;
      if (quantity <= 0) {
        next.splice(index, 1);
        return next;
      }
      next[index] = { ...target, quantity };
      return next;
    });
  }, []);

  const handleRemoveFromCart = useCallback((index: number) => {
    setCart((previous) => {
      const next = previous.map((line) => ({ ...line }));
      next.splice(index, 1);
      return next;
    });
  }, []);

  const handleOrderNoteChange = useCallback((note: string) => setOrderNote(note), []);

  const handleSetGuestProfile = useCallback(
    (profile: GuestProfile) => {
      const next = new Set(profile.favorites);
      setFavoriteIds(next);
      if (typeof window !== "undefined") {
        localStorage.setItem(favoritesStorageKey, JSON.stringify(Array.from(next)));
      }
      syncGuestProfilePreferences(Array.from(next), {
        consentPersonalization: profile.consentPersonalization ?? personalizationAllowed,
      });
    },
    [favoritesStorageKey, personalizationAllowed, syncGuestProfilePreferences],
  );

  const handlePhoneVerified = useCallback(
    ({ phone, guestName: verifiedName }: { phone: string; guestName: string }) => {
      const normalizedName = verifiedName.trim();
      setPhoneNumber(phone);
      setPhoneVerified(true);
      if (typeof window !== "undefined") {
        localStorage.setItem(phoneStorageKey, phone);
      }
      if (normalizedName.length > 0) {
        setGuestNameInput(normalizedName);
      }
      syncGuestProfilePreferences(Array.from(favoriteIds), {
        consentPersonalization: guestProfile?.consentPersonalization ?? personalizationAllowed,
        guestPhoneOverride: phone,
        guestNameOverride: normalizedName.length > 0 ? normalizedName : undefined,
      });
    },
    [
      favoriteIds,
      guestProfile?.consentPersonalization,
      personalizationAllowed,
      phoneStorageKey,
      setGuestNameInput,
      syncGuestProfilePreferences,
    ],
  );

  const handleUpdatePhone = useCallback(() => {
    setPhoneVerified(false);
  }, []);

  const handlePlaceOrder = useCallback(
    async (request: PlaceOrderRequest): Promise<PlaceOrderResult> => {
      if (cart.length === 0) {
        throw new Error("Add items to your cart first");
      }

      const cartSnapshot = cart.map((line) => ({ ...line }));
      const subtotalValue = cartSnapshot.reduce((sum, line) => sum + calculateLineTotal(line), 0);
      const tipValue = Math.max(0, request.tipAmount);
      const discountValue = Math.max(0, request.discountAmount);
      const taxValue = 0;
      const totalValue = subtotalValue - discountValue + tipValue + taxValue;

      const notesParts = [request.orderNote.trim()];
      if (request.scheduleOrder && request.readyWindow !== "asap") {
        notesParts.push(`Ready window: ${request.readyWindow}`);
      }
      const combinedNotes = notesParts.filter(Boolean).join("\n");

      if (isDemoMode) {
        const now = new Date();
        const readyTime = new Date(now.getTime() + 15 * 60_000);
        setCart([]);
        setOrderNote("");
        toast({ title: "Order placed!", description: "This is a demo confirmation." });
        return {
          orderNumber: `D-${Date.now().toString().slice(-4)}`,
          placedAt: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          estimatedReady: readyTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          cart: cartSnapshot,
          totals: {
            subtotal: subtotalValue,
            tax: taxValue,
            tip: tipValue,
            discount: discountValue,
            total: totalValue,
          },
        };
      }

      if (!tenantSlug || !tableSlug || tokenParam.length === 0) {
        throw new Error("Missing restaurant information");
      }

      const payload: CreatePublicOrderPayload = {
        token: tokenParam,
        tableSlug,
        type: request.fulfillment === "delivery" ? "delivery" : request.fulfillment === "dine-in" ? "dine-in" : "takeaway",
        guests: request.guests,
        guestName: request.name.trim() || undefined,
        guestPhone: phoneNumber.trim() || undefined,
        guestEmail: request.email.trim() || undefined,
        tipCents: Math.round(tipValue * 100),
        discountCents: Math.round(discountValue * 100),
        notes: combinedNotes || undefined,
        payMethod: request.paymentMethod,
        items: cartSnapshot.map((line) => ({
          itemId: line.item.id,
          qty: line.quantity,
          modifiers: line.modifiers.map((modifier) => modifier.id),
          notes: line.notes || undefined,
        })),
        guestPreferences: {
          favoriteItemIds: Array.from(favoriteIds),
          loyaltyOptIn: request.loyaltyUpdates,
          consent: {
            personalization: guestProfile?.consentPersonalization ?? personalizationAllowed,
          },
        },
        loyaltyRedeemPoints: request.loyaltyRedeemPoints > 0 ? request.loyaltyRedeemPoints : undefined,
      };

      const result = await createPublicOrder(tenantSlug, payload);

      if ((result as CreatePublicOrderResult as { queued?: boolean }).queued) {
        const queued = result as { queued: true; queueId: string };
        setCart([]);
        setOrderNote("");
        toast({ title: "Order queued", description: "We'll submit it as soon as we reconnect." });
        return {
          orderNumber: `Queued #${queued.queueId}`,
          placedAt: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          estimatedReady: null,
          cart: cartSnapshot,
          totals: {
            subtotal: subtotalValue,
            tax: taxValue,
            tip: tipValue,
            discount: discountValue,
            total: totalValue,
          },
        };
      }

      const response = result as CreatePublicOrderResponse;
      if (response.guest_token) {
        persistGuestToken(response.guest_token);
      }
      if (!isDemoMode) {
        const telemetry: Promise<void>[] = [];
        if (recommendationImpression?.id) {
          const recommendedSet = new Set(recommendationState.items.map((item) => item.id));
          for (const line of cartSnapshot) {
            if (!recommendedSet.has(line.item.id)) continue;
            const slotIndex = recommendationState.items.findIndex((item) => item.id === line.item.id);
            telemetry.push(
              recordRecommendationAction({
                tenantSlug,
                impressionId: recommendationImpression.id,
                action: "order_item",
                menuItemId: line.item.id,
                metadata: { orderId: response.id, quantity: line.quantity },
                guestProfileId: recommendationState?.guestProfileId ?? null,
                quantity: line.quantity,
                orderId: response.id,
                slot: slotIndex >= 0 ? slotIndex : undefined,
                surface: "table_order.hero",
                modelVersion: recommendationState?.modelVersion ?? CLIENT_RECOMMENDATION_MODEL_VERSION,
                holdout: recommendationState?.holdout ?? false,
              }).catch((error) => {
                console.error("[public] failed to record recommendation order action", error);
              }),
            );
          }
        }
        if (attachRecommendationImpression?.id && attachRecommendationState) {
          const attachSet = new Set(attachRecommendationState.items.map((entry) => entry.item.id));
          for (const line of cartSnapshot) {
            if (!attachSet.has(line.item.id)) continue;
            const slotIndex = attachRecommendationState.items.findIndex((entry) => entry.item.id === line.item.id);
            telemetry.push(
              recordRecommendationAction({
                tenantSlug,
                impressionId: attachRecommendationImpression.id,
                action: "order_item",
                menuItemId: line.item.id,
                metadata: {
                  orderId: response.id,
                  quantity: line.quantity,
                  source: "attach",
                  probability: attachRecommendationState.probabilities[line.item.id],
                },
                guestProfileId: null,
                quantity: line.quantity,
                orderId: response.id,
                slot: slotIndex >= 0 ? slotIndex : undefined,
                surface: ATTACH_SURFACE,
                modelVersion: attachRecommendationState.modelVersion ?? ATTACH_MODEL_VERSION,
                holdout: false,
              }).catch((error) => {
                console.error("[public] failed to record attach recommendation order action", error);
              }),
            );
          }
        }
        if (telemetry.length > 0) {
          void Promise.allSettled(telemetry);
        }
      }
      const favorites = response.guest_profile?.favorite_item_ids ?? [];
      if (favorites.length > 0) {
        setFavoriteIds(new Set(favorites));
        if (typeof window !== "undefined") {
          localStorage.setItem(favoritesStorageKey, JSON.stringify(favorites));
        }
      }

      const placedAtDate = new Date(response.placed_at);
      const readyAtDate = response.estimated_ready_at ? new Date(response.estimated_ready_at) : null;

      setCart([]);
      setOrderNote("");
      toast({ title: "Order placed!", description: `Confirmation ${response.code}` });

      return {
        orderNumber: response.code,
        placedAt: placedAtDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        estimatedReady: readyAtDate
          ? readyAtDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          : null,
        cart: cartSnapshot,
        totals: {
          subtotal: subtotalValue,
          tax: taxValue,
          tip: tipValue,
          discount: discountValue,
          total: totalValue,
        },
      };
    },
    [
      cart,
      calculateLineTotal,
      favoriteIds,
      favoritesStorageKey,
      guestProfile?.consentPersonalization,
      isDemoMode,
      persistGuestToken,
      personalizationAllowed,
      phoneNumber,
      recommendationImpression?.id,
      recommendationState.items,
      tenantSlug,
      tableSlug,
      tokenParam,
      toast,
    ],
  );

  const handleAssistantMessagesChange = useCallback((messages: AssistantMessage[], conversation: string | null) => {
    setAssistantMessages(messages);
    setAssistantConversationId(conversation);
  }, []);

  const resolveSafetyNotices = useCallback(
    (safety?: PublicAssistantSafety | null): string[] => {
      if (!safety) {
        return [];
      }
      const notices: string[] = [];
      if (safety.flags?.includes("profanity")) {
        notices.push(
          translate("public:assistant.safety.profanity", {
            defaultValue: "Let's keep it friendly-I'll loop in a teammate if you need anything else.",
          }),
        );
      }
      if (safety.flags?.includes("allergen")) {
        notices.push(
          translate("public:assistant.safety.allergen", {
            defaultValue: "Allergen note: I'm flagging this for the host stand to double-check.",
          }),
        );
      }
      if (safety.reasons?.includes("low_confidence")) {
        notices.push(
          translate("public:assistant.safety.lowConfidence", {
            defaultValue: "I'm looping in a teammate to confirm the details.",
          }),
        );
      }
      if (safety.reasons?.includes("repeated_clarification")) {
        notices.push(
          translate("public:assistant.safety.repeatedClarification", {
            defaultValue: "We flagged this so you don't have to repeat yourself.",
          }),
        );
      }
      if (safety.handoff) {
        notices.push(
          translate("public:assistant.safety.handoff", {
            defaultValue: "A teammate has been notified and will stop by shortly.",
          }),
        );
        toast({
          title: translate("public:assistant.safety.handoffToastTitle", {
            defaultValue: "Teammate notified",
          }),
          description: translate("public:assistant.safety.handoffToastDescription", {
            defaultValue: "Someone will swing by to help in person.",
          }),
        });
      }
      return notices;
    },
    [toast, translate],
  );

  const resolveActionNotices = useCallback(
    (actions?: PublicAssistantAction[] | null): string[] => {
      if (!actions || actions.length === 0) {
        return [];
      }
      const notices: string[] = [];
      actions.forEach((action) => {
        if (action.type === "add_to_cart") {
          const targetItem = menuItemMap.get(action.itemId);
          if (targetItem) {
            handleAddToCart(targetItem, action.quantity ?? 1, action.modifierIds ?? [], action.notes ?? "");
            notices.push(
              translate("public:assistant.actions.addToCart", {
                defaultValue: "Added {{item}} to your cart.",
                item: targetItem.name,
              }),
            );
            toast({
              title: translate("public:assistant.actions.addToCartToastTitle", {
                defaultValue: "Item added",
              }),
              description: targetItem.name,
            });
          } else {
            notices.push(
              translate("public:assistant.actions.addToCartMissing", {
                defaultValue: "I noted that request for the team.",
              }),
            );
          }
        } else if (action.type === "show_allergens") {
          const allergenList =
            action.allergenNames && action.allergenNames.length > 0
              ? action.allergenNames.join(", ")
              : translate("public:assistant.actions.showAllergensFallback", {
                  defaultValue: "We flagged your allergen request.",
                });
          notices.push(
            translate("public:assistant.actions.showAllergens", {
              defaultValue: "Allergen note: {{allergens}}.",
              allergens: allergenList,
            }),
          );
          toast({
            title: translate("public:assistant.actions.showAllergensToastTitle", {
              defaultValue: "Allergen flagged",
            }),
            description: allergenList,
          });
        }
      });
      return notices;
    },
    [handleAddToCart, menuItemMap, toast, translate],
  );

  const handleAssistantSend = useCallback(
    async (message: string, conversation: string | null, options?: { intentId?: string | null }) => {
      if (isDemoMode) {
        const responses =
          language === "en"
            ? [
                "Our Truffle Pasta is most popular! Features black truffle, pancetta, and fresh eggs with a 4.8-star rating.",
                "Yes! We have Caesar Salad, Caprese Salad, Mediterranean Mezze, and Chocolate Lava Cake for vegetarians.",
                "Try our Chocolate Lava Cake! Warm molten center with vanilla ice cream. 4.9-star rating!",
              ]
            : [
                "¡Nuestra Pasta con Trufa es la más popular! Con trufa negra, pancetta y huevos frescos. 4.8 estrellas.",
                "¡Sí! Tenemos Ensalada César, Caprese, Mezze Mediterráneo y Pastel de Lava de Chocolate vegetarianos.",
                "¡Prueba nuestro Pastel de Lava de Chocolate! Centro fundido con helado de vainilla. ¡4.9 estrellas!",
              ];
        return {
          reply: responses[Math.floor(Math.random() * responses.length) ?? 0],
          conversationId: conversation,
        };
      }
      if (!tenantSlug || !tableSlug || tokenParam.length === 0) {
        throw new Error("Assistant unavailable without table details.");
      }
      if (options?.intentId) {
        // Placeholder for future client-side telemetry hooks.
      }
      const normalizedGuestPhone =
        phoneVerified && phoneNumber.trim().length > 0 ? phoneNumber.trim() : undefined;
      const normalizedGuestName =
        typeof guestName === "string" && guestName.trim().length > 0 ? guestName.trim() : undefined;
      const response = await sendPublicAssistantMessage({
        tenantSlug,
        tableSlug,
        token: tokenParam,
        message,
        guestToken: guestToken ?? undefined,
        guestName: normalizedGuestName,
        guestPhone: normalizedGuestPhone,
        conversationId: conversation ?? undefined,
        locale: language,
      });
      const safetyNotices = resolveSafetyNotices(response.safety);
      const actionNotices = resolveActionNotices(response.actions);
      const mergedNotices = [...safetyNotices, ...actionNotices];
      const replyBody = mergedNotices.length > 0 ? `${mergedNotices.join("\n\n")}\n\n${response.response}` : response.response;
      return {
        reply: replyBody,
        conversationId: response.conversationId,
        quotaStatus: response.quota?.status ?? null,
        quotaMessage: response.quota?.message ?? null,
        resolutionTier: response.resolutionTier ?? null,
      };
    },
    [
      guestName,
      guestToken,
      isDemoMode,
      language,
      phoneNumber,
      phoneVerified,
      resolveActionNotices,
      resolveSafetyNotices,
      tableSlug,
      tenantSlug,
      tokenParam,
    ],
  );

  const assistantEnabled = Boolean(tableData?.assistant?.enabled);
  const assistantDisabledReason = assistantEnabled
    ? null
    : language === "es"
    ? "El asistente no está disponible en este momento."
    : "The assistant is unavailable right now.";

  const pendingGuestTokenRef = useRef<string | null>(null);
  useEffect(() => {
    if (isDemoMode || !tenantSlug || !phoneVerified) {
      return;
    }
    const normalizedPhone = phoneNumber.trim();
    if (!normalizedPhone || guestToken) {
      return;
    }
    if (pendingGuestTokenRef.current === normalizedPhone) {
      return;
    }
    pendingGuestTokenRef.current = normalizedPhone;
    let cancelled = false;
    syncGuestProfile({
      tenantSlug,
      guestPhone: normalizedPhone,
      guestName: guestName.trim().length > 0 ? guestName.trim() : undefined,
    })
      .then((response) => {
        if (!cancelled && response.guestToken) {
          persistGuestToken(response.guestToken);
        }
      })
      .catch((error) => {
        console.error("[public] failed to bootstrap guest token", error);
      })
      .finally(() => {
        pendingGuestTokenRef.current = null;
      });
    return () => {
      cancelled = true;
    };
  }, [guestName, guestToken, isDemoMode, persistGuestToken, phoneNumber, phoneVerified, tenantSlug]);

  if (linkIssues.length > 0) {
    return (
      <>
        <LinkIssuePage issues={linkIssues} copy={linkIssueCopy} />
      </>
    );
  }

  if (!phoneVerified) {
    return (
      <>
        <PhoneVerificationPage
          onVerified={handlePhoneVerified}
          language={language}
          initialPhone={phoneNumber}
          initialName={guestName}
          venueName={venueDisplayName}
        />
      </>
    );
  }

  if (menuQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <p className="text-sm text-zinc-600">Loading menu...</p>
      </div>
    );
  }

  if (menuQuery.isError && !tableData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <div className="rounded-xl border border-zinc-200 bg-white p-6 text-center shadow-sm">
          <h2 className="mb-2 text-base font-medium">Something went wrong</h2>
          <p className="text-sm text-zinc-600">We couldn't load this menu. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <MenuPage
        items={menuItems}
        categories={menuCategories}
        venue={venueInfo}
        phoneNumber={phoneNumber}
        onUpdatePhone={handleUpdatePhone}
        cartItemCount={cart.reduce((sum, line) => sum + line.quantity, 0)}
        cart={cart}
        orderNote={orderNote}
        onOrderNoteChange={handleOrderNoteChange}
        onAddToCart={handleAddToCart}
        onUpdateCartItem={handleUpdateCartItem}
        onRemoveFromCart={handleRemoveFromCart}
        onPlaceOrder={handlePlaceOrder}
        guestProfile={guestProfile}
        setGuestProfile={handleSetGuestProfile}
        language={language}
        setLanguage={setLanguageState}
        subtotal={subtotal}
        recommendations={{
          items: recommendationState.items,
          source: recommendationState.source,
          breakdown: recommendationState.breakdown,
          consentPersonalization: recommendationState.consentPersonalization,
          onAction: handleRecommendationAction,
        }}
        attachRecommendations={
          attachRecommendationState
            ? {
                items: attachRecommendationState.items,
                onAction: handleAttachRecommendationAction,
              }
            : undefined
        }
        loyaltyProgram={tableData?.loyalty ?? null}
        loyaltyBalance={guestProfilePayload?.loyalty_points ?? null}
      />
      <AIAssistantPage
        language={language}
        initialMessages={assistantMessages}
        initialConversationId={assistantConversationId}
        onMessagesChange={handleAssistantMessagesChange}
        onSendMessage={handleAssistantSend}
        resolveInstantAnswer={resolveAssistantInstantAnswer}
        disabled={!assistantEnabled}
        disabledReason={assistantDisabledReason}
      />
    </>
  );
}
